import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useMediaQuery } from "@mui/material";

import { my } from "@store/actions/user";
import { useAppDispatch } from "@store/hooks";
import instance from "@store/middleware/api";
import { openSubscriptionCheckout, openSubscriptionUpgrade, openSwitchToAnnualCheckout } from "@store/reducers/cart";

import { bannerSurface } from "@utils/bannerGradient";
import { ERoutes } from "@utils/constants";
import { findAnnouncableCancelledSub } from "@utils/subscriptionHistory";
import styled, { keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";
import { ScrollChevron, ScrollHint, useScrollAffordance } from "@/components/CartModal/phoneTemplate";
import { WhitelistingBtn, WhitelistingStepper, WhitelistingText } from "@/components/LicenseModal/styles";
import UpdatePaymentMethodModal from "@/components/UpdatePaymentMethodModal";
import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { readDevBillingPeriod } from "@/contexts/DevAccountSwitcher";
import { useAuth } from "@/hooks/auth";
import { useModalAnimation } from "@/hooks/useModalAnimation";
import { useOpenReactivationCheckout } from "@/hooks/useOpenReactivationCheckout";
import { useSubscriptionConfig } from "@/hooks/useSubscriptionConfig";
import AccountLayout from "@/layouts/AccountLayout";
import { openUpdatePaymentModal } from "@/layouts/components/GlobalUpdatePaymentModal";
import { colors, fonts, fontSizes, fontWeights, radius, transitions } from "@/styles/design-system";
import { BannerCTA, BannerShell } from "@/styles/shared-components";
import { computeSubscriptionPricing } from "@/utils/pricing";
import { clearSubscriptionAlert, readSubscriptionAlert, writeSubscriptionAlert } from "@/utils/subscriptionAlert";

/* ── Blue accent icons for the gold CTAs. Uses the footer-newsletter darker
     blue (#60a3d7), which reads better than the light blue on top of gold. ── */
const GOLD_BTN_ICON_BLUE = "#60a3d7";
const BlueDiamond = ({ color = GOLD_BTN_ICON_BLUE }: { color?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0 }}>
    <path d="M6 3h12l4 6-10 13L2 9Z" />
  </svg>
);
const BluePlus = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={GOLD_BTN_ICON_BLUE} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const BlueCard = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={GOLD_BTN_ICON_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="1" y="4" width="22" height="16" rx="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);
const BlueCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GOLD_BTN_ICON_BLUE} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
/** Round-arrow "start again" glyph — the same one the global reactivate bar
    uses, so the two Reactivate buttons look like one action. */
const ReactivateArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GOLD_BTN_ICON_BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

/* ═══════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════ */

/* ── Cross-fading section title — swaps smoothly between Current/New
   labels when the user toggles claims/channels into downgrade territory.
   Two-stage transition: fade the old text out, swap content at full
   transparency, fade the new text in. Less blinky than a single fade-in
   from opacity 0 because both halves of the transition are perceptually
   smooth — no instant disappearance, no jump cut. */

const SmoothLabel: React.FC<{ text: string }> = ({ text }) => {
  const [displayed, setDisplayed] = React.useState(text);
  const [fading, setFading] = React.useState(false);
  React.useEffect(() => {
    if (text === displayed) return;
    setFading(true);
    const t = window.setTimeout(() => {
      setDisplayed(text);
      setFading(false);
    }, 180);
    return () => window.clearTimeout(t);
  }, [text, displayed]);
  return (
    <span style={{
      display: "inline-block",
      opacity: fading ? 0 : 1,
      transition: "opacity 180ms ease",
    }}>
      {displayed}
    </span>
  );
};

/* SmoothLayout — same fade-out → swap → fade-in pattern for an entire
   sub-tree. `version` is a stable id of "which layout is rendering" so
   when the user crosses a config boundary (no-change → upgrade-card,
   or → downgrade-card), the old JSX stays mounted through the fade-out
   before the new JSX swaps in. */

/* SmoothLayout — opacity-only cross-fade between content variants.
   Height management is intentionally NOT done here: the parent modal has
   its own height system (`bodyRef`/`prevHeightRef`) that animates the
   ModalBody. SmoothLayout signals via the `onSwap` callback when its
   180ms fade completes so the modal can re-run its height calc with the
   new content's `scrollHeight`. */
const SmoothLayout: React.FC<{ version: string; children: React.ReactNode; onSwap?: () => void }> = ({ version, children, onSwap }) => {
  const [snapshot, setSnapshot] = React.useState<React.ReactNode | null>(null);
  const [activeVersion, setActiveVersion] = React.useState(version);
  const [fading, setFading] = React.useState(false);
  const prevChildrenRef = React.useRef<React.ReactNode>(children);
  const onSwapRef = React.useRef(onSwap);
  onSwapRef.current = onSwap;

  if (version === activeVersion && snapshot === null) {
    prevChildrenRef.current = children;
  }

  React.useLayoutEffect(() => {
    if (version === activeVersion) return;
    setSnapshot(prevChildrenRef.current);
    setFading(true);
    const t = window.setTimeout(() => {
      setSnapshot(null);
      setActiveVersion(version);
      setFading(false);
      onSwapRef.current?.();
    }, 180);
    return () => window.clearTimeout(t);
  }, [version, activeVersion]);

  return (
    <div style={{
      opacity: fading ? 0 : 1,
      transition: "opacity 180ms ease",
    }}>
      {snapshot ?? children}
    </div>
  );
};


/* ── Status Banner ── */

const StatusBanner = styled.div<{ $active?: boolean }>`
  padding: 20px 22px;
  border-radius: ${radius["2xl"]};
  @media (max-width: 600px) {
    padding: 16px 14px;
  }
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(52, 211, 153, 0.1) 0%, rgba(52, 211, 153, 0.04) 100%)"
      : "rgba(255, 255, 255, 0.03)"};
  border: 1px solid ${({ $active }) =>
    $active ? "rgba(52, 211, 153, 0.35)" : colors.border.light};
  margin-bottom: 24px;
`;


const StatusDesc = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.text.muted};
  margin-top: 2px;
`;

/* Free pill + its description: side-by-side on desktop, stacked (description
   below the pill) on phone. */
const PillDescRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

/* The Premium tier pill in the "Subscription — Start Today" heading. Inline
   beside the heading text on desktop; drops to its own line (heading text
   below it) on phone. */
const PremiumTitlePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px 3px;
  border-radius: 8px;
  background: ${colors.tier.subscription.bg};
  color: ${colors.tier.subscription.text};
  font-family: ${fonts.number};
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  vertical-align: middle;
  position: relative;
  top: -2px;
  margin-right: 8px;

  @media (max-width: 600px) {
    display: flex;
    width: fit-content;
    top: 0;
    margin-right: 0;
    margin-bottom: 10px;
  }
`;

/* ── Section Card ── */

const SectionCard = styled.div<{ $accent?: boolean }>`
  position: relative;
  padding: 22px 22px;
  border-radius: ${radius["2xl"]};
  @media (max-width: 600px) {
    padding: 16px 14px;
  }
  background: ${({ $accent }) =>
    $accent
      ? "linear-gradient(145deg, rgba(186, 144, 92, 0.08) 0%, rgba(81, 58, 40, 0.5) 100%)"
      : colors.surface.card};
  border: 1px solid ${({ $accent }) =>
    $accent ? "rgba(235, 194, 142, 0.18)" : colors.border.light};
  margin-bottom: 24px;
  overflow: hidden;

  ${({ $accent }) =>
    $accent &&
    `&::before {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 1px;
      background: ${colors.gold.gradient};
      opacity: 0.4;
    }`}
`;




const PlanTitle = styled.h2`
  font-family: ${fonts.body};
  font-size: 1.25rem;
  font-weight: 600;
  color: ${colors.text.heading};
  margin: 0;
`;

const PriceBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

const PriceValue = styled.span`
  font-family: ${fonts.number};
  font-size: 2rem;
  font-weight: 700;
  color: ${colors.accent.blue};
  line-height: 1;
`;

const PriceUnit = styled.span`
  font-family: ${fonts.number};
  font-size: 0.875rem;
  color: ${colors.accent.blue};
`;


/* ── Billing Toggle (switch style) ── */

const BillingToggleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BillingSwitch = styled.button<{ $annual: boolean }>`
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: 1px solid ${({ $annual }) => ($annual ? "rgba(52, 211, 153, 0.5)" : "rgba(255, 255, 255, 0.15)")};
  background: ${({ $annual }) => ($annual ? "rgba(52, 211, 153, 0.6)" : "rgba(255, 255, 255, 0.06)")};
  cursor: pointer;
  position: relative;
  padding: 0;
  transition: all ${transitions.normal};
  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${({ $annual }) => ($annual ? "20px" : "3px")};
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${({ $annual }) => ($annual ? "#fff" : "rgba(255, 255, 255, 0.35)")};
    transition: left ${transitions.normal};
  }
`;

const BillingLabel = styled.span<{ $active: boolean }>`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 400;
  color: ${colors.text.primary};
  cursor: pointer;
`;


/* ── Steppers ── */

/* Wraps the channel + claim rows. Desktop ≥1150 keeps the original joined-card
   look (two cards sharing a divider). Phone/tablet ≤1149 splits them into two
   separate slim pills like the /features amount pickers. */
const StepperGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;

  @media (min-width: 1150px) {
    & > div:first-child {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
    & > div:last-child {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-top: none;
    }
  }

  @media (max-width: 1149px) {
    gap: 8px;
  }
`;

const StepperCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(235, 194, 142, 0.25);
  border-radius: ${radius.xl};

  /* Phone/tablet: slim rounded pill matching the /features amount pickers. */
  @media (max-width: 1149px) {
    min-height: 34px;
    padding: 6px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.06);
    gap: 8px;
  }
  @media (max-width: 600px) {
    min-height: 46px;
  }
`;

const StepperValue = styled.div`
  font-family: ${fonts.number};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${colors.text.primary};

  /* Phone/tablet: the value sits BETWEEN the −/+ buttons (the /features picker
     layout) — flex into the middle and centre, lighter weight. Desktop keeps
     value-left / controls-right (untouched). The inline "changed → blue + 600"
     highlight still overrides this when the user edits the value. */
  @media (max-width: 1149px) {
    order: 1;
    flex: 1;
    min-width: 0;
    text-align: center;
    font-size: 0.875rem;
    font-weight: 400;
  }
  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const StepperControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  /* Phone/tablet: dissolve the controls wrapper so the −/+ become direct
     children of the StepperCard flex row; combined with StepperValue's
     order/flex this yields [ −  …value…  + ]. Desktop unaffected. */
  @media (max-width: 1149px) {
    display: contents;
  }
`;

/* Section label with side lines — same visual treatment as the cart's
   SectionLabel ("YOUR CONFIGURATION", etc.) so the manage modal matches
   the checkout flow. */
const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 4px;
  margin-bottom: 12px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(235, 194, 142, 0.2);
  }

  span {
    color: #fff;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    white-space: nowrap;
  }
`;

const StepperBtn = styled.button<{ $hidden?: boolean }>`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  border-radius: 50%;
  border: none;
  background: ${colors.accent.blue};
  color: ${colors.brown.darkest};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color ${transitions.fast}, color ${transitions.fast}, border-color ${transitions.fast};
  visibility: ${({ $hidden }) => ($hidden ? "hidden" : "visible")};
  /* Hover only on real pointers — touch leaves :hover stuck after a tap. */
  @media (hover: hover) {
    &:hover { background: ${colors.accent.blueLight}; }
  }
  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
  }

  /* Phone/tablet: outline blue button matching the /features amount pickers
     (faint fill + blue border + blue glyph), bigger tap target on phone. */
  @media (max-width: 1149px) {
    width: 22px;
    height: 22px;
    background: rgba(156, 211, 253, 0.1);
    border: 1px solid rgba(156, 211, 253, 0.4);
    color: ${colors.accent.blue};
    @media (hover: hover) {
      &:hover {
        background: rgba(156, 211, 253, 0.2);
        border-color: ${colors.accent.blue};
        color: #ffffff;
      }
    }
    &:disabled {
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 255, 255, 0.12);
      color: rgba(255, 255, 255, 0.3);
    }
  }
  @media (max-width: 600px) {
    width: 34px;
    height: 34px;
  }
`;

/* Fixed-pixel glyph box. Locks the optical center of the −/+ inside the
   button regardless of the button's font-metric inheritance. */
const StepperGlyph = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  font-family: ${fonts.number};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;

  /* phone: scale the glyph up with the larger tap target */
  @media (max-width: 600px) {
    width: 18px;
    height: 18px;
    font-size: 18px;
  }
`;



const FeatureItem = styled.div<{ $muted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${({ $muted }) => ($muted ? "rgba(251, 244, 236, 0.45)" : colors.text.primary)};
  line-height: 1.7;
`;

const FeatureDot = styled.span<{ $type: "check" | "cross" }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.6rem;
  padding-top: 1px;
  background: ${({ $type }) => ($type === "check" ? "rgba(52, 211, 153, 0.12)" : "rgba(228, 122, 106, 0.1)")};
  color: ${({ $type }) => ($type === "check" ? colors.accent.green : colors.accent.red)};
`;

/* ── CTA Button ── */

const CTAButton = styled.button<{ $variant?: "primary" | "secondary" | "danger" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 28px;
  border-radius: ${radius.md};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: ${fontWeights.semibold};
  cursor: pointer;
  transition: all ${transitions.normal};

  ${({ $variant }) =>
    $variant === "secondary"
      ? `background: transparent; border: 1.5px solid rgba(235, 194, 142, 0.4); color: ${colors.gold.text}; &:hover { border-color: rgba(235, 194, 142, 0.6); }`
      : $variant === "danger"
        ? `background: #d4645a; border: 1.5px solid #d4645a; color: #fff; padding: 12px 20px; &:hover { background: ${colors.accent.red}; border-color: ${colors.accent.red}; }`
        : `background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%); border: none; color: ${colors.brown.darkest}; box-shadow: 0 0 24px rgba(186, 144, 92, 0.25); &:hover { box-shadow: 0 4px 32px rgba(186, 144, 92, 0.45); }`}
`;

/* ── Cancel Link ──
   Calm ghost-button treatment for a low-emphasis destructive action:
   transparent fill, hairline neutral border, muted white label.
   Stays quiet — no red, no gold. Hover lifts the border + fill subtly. */

const spinAnim = keyframes`
  to { transform: rotate(360deg); }
`;
const InlineSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 8px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spinAnim} 0.7s linear infinite;
  vertical-align: -2px;
`;

const CancelLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 11px 22px;
  border-radius: ${radius.md};
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.7);
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 500;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all ${transitions.normal};

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.32);
    color: rgba(255, 255, 255, 0.95);
  }

  &:focus-visible {
    outline: 1px solid rgba(255, 255, 255, 0.45);
    outline-offset: 2px;
  }
`;


/* ── Usage Bar ── */

const UsageBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const UsageTrack = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
`;

const UsageFill = styled.div<{ $pct: number }>`
  height: 100%;
  border-radius: 3px;
  width: ${({ $pct }) => Math.min(100, $pct)}%;
  background: ${({ $pct }) =>
    $pct >= 100 ? "linear-gradient(90deg, #e47a6a, #f09080)"
    : $pct >= 80 ? "linear-gradient(90deg, #f5a623, #ffcc66)"
    : `linear-gradient(90deg, ${colors.accent.green}, #6ee7b7)`};
  transition: width 0.4s ease;
`;

const UsageText = styled.span`
  font-family: ${fonts.number};
  font-size: ${fontSizes.sm};
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  min-width: 60px;
  text-align: right;
`;

/* ── Two Column Layout (free view) ── */

const TwoColLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: ${radius.xl};
`;


/* ═══════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════ */

const STATUS_MAP: Record<string, { title: string; desc: string; active: boolean }> = {
  FREE: { title: "You are on the Free Plan", desc: "Upgrade to Subscription for full library access, WAV downloads, and claim auto-resolving.", active: false },
  SUBSCRIPTION: { title: "Subscription", desc: "Full library access as MP3 + WAV. Auto-resolve claims.", active: true },
  MUSIC_CREATOR: { title: "Music Creator Program", desc: "Revenue split via SongMate. Credit text and timestamps required.", active: true },
};

/* ═══════════════════════════════════════════
   MANAGE ADD-ONS MODAL
   ═══════════════════════════════════════════ */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  /* flex-start, not center, because the overlay scrolls: a centred flex item
     taller than its container has its top cut off and unreachable. ModalCard
     carries an auto vertical margin instead, which centres it while it fits and
     stops centring the moment it does not. */
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 24px 0;
  background: transparent;

  /* Phone: small side gap so the modal never touches the screen edges
     (modal-template-phone.md), and the card stays bounded to the viewport
     there, so ordinary centring is fine. */
  @media (max-width: 600px) {
    align-items: center;
    padding: 0 14px;
  }
`;

const ModalCard = styled.div<{ $closing?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 500px;
  /* dvh, never vh — tracks the visible viewport as the iOS bar shows/hides so
     the body stays correctly bounded (per docu/modal-template-phone.md).
     Phone only: above 600px the card grows to its content and the OVERLAY
     scrolls, so a long configuration is read by scrolling the page-like overlay
     rather than an inner pane. */
  max-height: calc(100dvh - 48px);

  @media (min-width: 601px) {
    max-height: none;
    margin: auto 0;
  }
  /* Static gradient — mirrors Dialog/components.tsx .MuiDialog-paper so
     this modal stays in lockstep with the rest of the modal surface.
     Replaces the legacy live-blur frosted glass which read differently
     on dark vs bright pages. See project-modal-gradient memory. */
  background:
    radial-gradient(
      ellipse 45% 40% at 42% -3%,
      rgba(176, 144, 105, 0.9) 0%,
      rgba(160, 128, 90, 0.5) 35%,
      transparent 75%
    ),
    radial-gradient(
      ellipse 55% 50% at 58% 3%,
      rgba(170, 140, 100, 0.75) 0%,
      rgba(140, 110, 78, 0.35) 45%,
      transparent 85%
    ),
    radial-gradient(
      ellipse 70% 35% at 50% 8%,
      rgba(150, 118, 82, 0.4) 0%,
      transparent 70%
    ),
    linear-gradient(
      180deg,
      rgb(153 112 75) 0%,
      rgb(99, 74, 54) 50%,
      rgb(111 83 60) 100%
    );
  border: none;
  border-radius: 24px;
  /* Clip the full-width tab divider's edge-breakout to the rounded card edge.
     No-op on desktop (divider lands exactly at the edge there); on phone the
     tighter head inset makes the divider over-reach, and this trims it clean.
     Does not clip the element's own scrim box-shadow. */
  overflow: hidden;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7), 0 20px 60px rgba(0, 0, 0, 0.6);
  opacity: ${({ $closing }) => ($closing ? 0 : 1)};
  transform: translateY(${({ $closing }) => ($closing ? "8px" : "0")});
  transition: opacity 0.25s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ModalHead = styled.div`
  flex-shrink: 0;
  /* No top padding on any width. The title carries its own 68px band centred on
     the ✕ (see ModalTitle), which is how every other modal builds this row —
     32px of head padding on top of that band pushed the heading well below the
     close button and left a dead strip above it. */
  padding: 0 32px;

  /* Phone: tighter side inset to match the body/footer. The full-width tab
     divider still breaks out to the card edge (clipped by ModalCard's
     overflow:hidden). */
  @media (max-width: 600px) {
    padding: 0 20px;
  }
`;

const ModalBody = styled.div`
  flex: 0 0 auto;
  overflow-y: hidden;
  /* Top gap clears the tabs. It is 4px short of TabRow's 20px top padding
     because TabRow already carries 4px below the pills for their glow — the two
     add up, so the gap a reader sees above and below the pills is equal. Keep
     them in step if either changes. No bottom inset: whatever ends the body has
     its own bottom margin stripped below, and the footer brings its own top
     padding. */
  padding: 16px 32px 0 32px;

  /* Phone: tighter side inset gives the −/+ picker more room. Same arithmetic
     against TabRow's 16px. */
  @media (max-width: 600px) {
    padding: 12px 20px 0 20px;
  }

  /* Whatever ends the body must not add a gap of its own. The payment blocks
     each carry a bottom margin so they can stack, and one of them is always the
     last thing in here — so that margin became dead space below the amount once
     the body dropped its bottom inset. Reached two levels down because the block
     sits inside the cross-fade wrapper, and with !important because those
     margins are inline styles on variants that swap. */
  > div > :last-child,
  > div > :last-child > :last-child {
    margin-bottom: 0 !important;
  }

  &::-webkit-scrollbar { width: 12px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(235, 194, 142, 0.5);
    border-radius: 6px;
    border: 4px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(235, 194, 142, 0.7);
    background-clip: padding-box;
  }
`;

const ModalFooter = styled.div.attrs({ "data-modal-footer": "" })`
  flex-shrink: 0;
  position: relative; /* anchors the scroll-affordance fade over the body bottom */
  padding: 20px 32px 32px;

  /* Phone: match the body's tighter side inset + a little less bottom space. */
  @media (max-width: 600px) {
    padding: 18px 20px 24px;
  }
`;

/* Scroll affordance for the scrollable ModalBody — phone/tablet standard
   (modal-template-phone.md): iOS never paints a persistent scrollbar for touch
   overflow, so a tall body can cut off at a clean line that reads as "the end".
   Anchored to the footer's top edge (bottom:100%) so it overlays the body's
   last 52px without disturbing the body-height measurement. Desktop ≥1150 has
   the native scrollbar, so it's hidden there. */
const ScrollFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 52px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;
  background: linear-gradient(to bottom, rgba(111, 83, 60, 0) 0%, rgba(111, 83, 60, 0.8) 52%, rgba(111, 83, 60, 1) 100%);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s ease;

  @media (min-width: 1150px) {
    display: none;
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(235, 194, 142, 0.3);
  background: rgba(235, 194, 142, 0.2);
  color: #fff;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background ${transitions.fast};
  &:hover { background: rgba(235, 194, 142, 0.35); }
`;

const ModalTitle = styled.h2`
  /* Cormorant Garamond — same heading font as the other modals (cart
     checkout StepTitle, LicenseModal, etc.) instead of Raleway. */
  font-family: ${fonts.heading};
  font-size: 1.5rem;
  font-weight: 400;
  color: ${colors.text.heading};

  /* The title is a 68px band so it sits on the ✕'s own centre line (top 16 +
     36 tall → centre 34; 16 + 36 + 16 = 68). Right padding clears the button so
     a long heading never runs underneath it. Same construction as the other
     modals — the difference on phone is only that it centres horizontally.

     The 3px of padding-top is an optical correction, not a layout one. Cormorant
     Garamond's ascent and descent together exceed its line-height, so the glyphs
     sit high inside their own line box and geometric centring lands the words
     visibly above the ✕. box-sizing: border-box keeps the band at 68px, so 6px
     of top padding moves the text down by half that. */
  box-sizing: border-box;
  display: flex;
  align-items: center;
  line-height: 1.2;
  min-height: 68px;
  margin: 0;
  padding: 6px 44px 0 0;

  /* Phone reads left-aligned like the desktop head, and without the optical
     nudge — at this size it put the heading visibly below the ✕ rather than
     level with it. Right inset still clears the button (16px offset + 36px
     wide, inside the head's 20px padding). */
  @media (max-width: 600px) {
    padding: 0 40px 0 0;
  }
`;

/* Tab row (Configuration / Plan). Left-aligned at every width, with the heading
   above it. */
const TabRow = styled.div`
  display: flex;
  /* Pills, the same ones the claims page uses. They were folder tabs on a
     full-width dark strip with a bottom border the active tab overlapped —
     which is why the strip and the negative margins were here. A pill carries
     its own edge, so the strip has nothing left to do. */
  gap: 8px;
  padding: 20px 0 4px;

  @media (max-width: 600px) {
    padding: 16px 0 4px;
  }
`;


interface ManageAddonsModalProps {
  open: boolean;
  onClose: () => void;
  /** The config the user is effectively paying for NOW. Used as the
      baseline for upgrade/downgrade detection. */
  currentChannels: number;
  currentClaims: number;
  billingPeriod: "monthly" | "annual";
  daysLeftInPeriod: number;
  periodEndDate?: string; // ISO date string
  /** Total days in the current billing cycle (period_end − period_start).
      Used as the denominator for prorated credit math on monthly upgrades —
      avoids the 31-day-month over-credit / 28-day-month under-credit bug.
      Defaults to 30 when omitted. */
  cycleLengthDays?: number;
  /** Pending billing-period switch — set to `"monthly"` when an annual user
      has scheduled an annual→monthly switch (Stripe sub `cancel_at_period_end`
      is also true in this case). Drives the Plan tab to show the switch-pending
      block + cancel-switch CTA instead of the normal billing-period block. */
  pendingBillingPeriod?: "monthly" | null;
  /** Optional starting values for the channel/claim steppers. Defaults to
      `currentChannels` / `currentClaims` when omitted. Used by the Restore
      Plan flow to pre-fill the user's OLD config while keeping upgrade
      detection honest (current = what Stripe has now, initial = what we
      suggest the user restore to). */
  initialChannels?: number;
  initialClaims?: number;
  /** When true, the subscription is scheduled to cancel at period end —
      steppers are disabled because there's no longer a future cycle to bill
      a config change against. User must Reactivate first. */
  isCancelling?: boolean;
  /** Called when the user clicks the inline "reactivate" link in the
      cancelling-state alert. The page owns the actual reactivation flow
      (it has the success-banner + DEV state plumbing). */
  onReactivate?: () => void;
  /** Called when the user clicks "Schedule Downgrade". Receives the new
      channels + claims values. The page owns the API call, success-banner
      + DEV state plumbing (same pattern as `onReactivate`). */
  onScheduleDowngrade?: (channels: number, claims: number) => void;
  /** Called when the user clicks "Switch to Monthly Payment". Page owns
      the POST + Redux user refetch + sub refetch so the header/menu reflect
      the new pending state without a hard reload. */
  onSwitchToMonthly?: () => void;
  /** Called when the user clicks "Keep Annual" / Cancel Switch from the
      Manage modal's Plan tab. Same ownership pattern as above. */
  onCancelBillingPeriodSwitch?: () => void;
  /** Which tab to land on when the modal opens. Defaults to `"config"`.
      AccountDashboard passes `"plan"` when the modal is opened from the
      DeleteAccountDialog's "Manage Subscription" CTA so the cancel
      controls are visible immediately. */
  initialTab?: "config" | "plan";
  /** True when the subscription is PAST_DUE. Blocks plan-config changes (the
      Configuration tab is disabled) while keeping the Plan/Cancel tab usable so
      the user can still cancel before fixing payment. */
  isPastDue?: boolean;
  /** True when the paid period ran out during a credit pause (ToU 17.4). Locks
      the same controls as PAST_DUE and for the same reason: there is no live
      paid period to change. Sending this subscription through the upgrade path
      would charge a proration against a period that already ended, leave the
      Stripe pause in place and never clear the awaiting state. Reactivating is
      the route — that checkout is where the configuration is chosen. Cancel
      stays reachable on the Plan tab. */
  isAwaitingReactivation?: boolean;
  /** Opens the prefilled reactivation checkout from the notice above. */
  onReactivateNow?: () => void;
}

export const ManageAddonsModal: React.FC<ManageAddonsModalProps> = ({ open, onClose, currentChannels, currentClaims, billingPeriod, daysLeftInPeriod, periodEndDate, cycleLengthDays, pendingBillingPeriod, initialChannels, initialClaims, isCancelling, onReactivate, onScheduleDowngrade, onSwitchToMonthly, onCancelBillingPeriodSwitch, initialTab, isPastDue, isAwaitingReactivation, onReactivateNow }) => {
  // Both states mean the same thing for the configuration controls: no live
  // paid period, so nothing to prorate against.
  const configLocked = !!isPastDue || !!isAwaitingReactivation;
  const dispatch = useAppDispatch();
  const { mounted, closing } = useModalAnimation(open);
  const [entered, setEntered] = useState(false);
  const subConfig = useSubscriptionConfig();

  const [newChannels, setNewChannels] = useState(initialChannels ?? currentChannels);
  const [newClaims, setNewClaims] = useState(initialClaims ?? currentClaims);

  useEffect(() => {
    if (mounted && !closing) {
      requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
    } else {
      setEntered(false);
    }
  }, [mounted, closing]);

  useEffect(() => {
    if (open) {
      setNewChannels(initialChannels ?? currentChannels);
      setNewClaims(initialClaims ?? currentClaims);
    }
  }, [open, currentChannels, currentClaims, initialChannels, initialClaims]);

  const isAnnualSub = billingPeriod === "annual";
  // Monthly users can preview annual pricing in-modal by toggling the
  // "Switch to annual" switch. The toggle is a UI-only flip — the actual
  // subscription stays monthly until the user clicks the bottom Upgrade
  // button, at which point we dispatch the switch-to-annual checkout.
  const [previewAnnual, setPreviewAnnual] = useState(false);
  useEffect(() => { if (!open) setPreviewAnnual(false); }, [open]);
  const isAnnual = isAnnualSub || previewAnnual;
  const previewingSwitch = !isAnnualSub && previewAnnual;

  // Centralized pricing (claims billed per channel) — mirrors the backend.
  const oldPricing = computeSubscriptionPricing(subConfig, { channels: currentChannels, claims: currentClaims, isAnnual });
  const newPricing = computeSubscriptionPricing(subConfig, { channels: newChannels, claims: newClaims, isAnnual });
  const premiumBase = newPricing.baseMonthly;

  const oldExtraChannels = oldPricing.extraChannels;
  const oldExtraClaimPacks = oldPricing.extraClaimPacks;
  const oldExtra = oldPricing.addonsMonthly;
  const newExtraChannels = newPricing.extraChannels;
  const newExtraClaimPacks = newPricing.extraClaimPacks;
  const newExtra = newPricing.addonsMonthly;
  const newTotal = newPricing.totalMonthly;
  // For the upgrade-detection math, compare new against the user's CURRENT
  // sub config evaluated at the current sub's billing period — otherwise a
  // monthly user toggling preview-annual would always look like an "upgrade"
  // just from the cadence change. The previewing-switch case is handled by
  // its own confirm path below.
  const oldPremiumBase = isAnnualSub
    ? Math.round(subConfig.basePriceMonthly * (1 - subConfig.annualDiscountPercent / 100) * 100) / 100
    : subConfig.basePriceMonthly;
  const oldTotal = oldPremiumBase + oldExtra;
  const hasChanges = newChannels !== currentChannels || newClaims !== currentClaims || previewingSwitch;
  const diff = newTotal - oldTotal;
  // Direction by quantity, not by cost. A bump within an included tier (e.g.
  // 1ch → 2ch when includedChannels is 2) has cost diff = 0 but is still a
  // legit upgrade — the user wants more capacity *now*. Cost-only detection
  // would route it into the "Schedule Downgrade" branch, which is wrong.
  // Fall back to cost-only when quantities are equal (covers monthly→annual
  // preview where channels/claims didn't change but base price did).
  const qtyDelta = (newChannels - currentChannels) + (newClaims - currentClaims);
  const isUpgrade = previewingSwitch || qtyDelta > 0 || (qtyDelta === 0 && diff > 0);
  const isDowngrade = !previewingSwitch && (qtyDelta < 0 || (qtyDelta === 0 && diff < 0));

  const handleClose = useCallback(() => { onClose(); }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  // Effective days remaining in the current cycle. With the cycle-reset
  // upgrade model there are no grace/standard branches anymore — this
  // is just the raw `daysLeftInPeriod` prop.
  const effectiveDaysLeft = daysLeftInPeriod;
  const [tab, setTabRaw] = useState<"config" | "plan">("config");
  const [cancelConfirm, setCancelConfirmRaw] = useState(false);
  // Confirmation step for "Switch to Monthly Payment" — surfaces the
  // savings the user is about to give up and when the change takes effect.
  const [switchToMonthlyConfirmOpen, setSwitchToMonthlyConfirmOpen] = useState(false);
  // Fade the confirm out (240ms) before unmounting; run the follow-up action
  // (e.g. close the manage modal + fire the switch) AFTER the fade so the close
  // reads smooth instead of both modals snapping shut at once.
  const [switchConfirmClosing, setSwitchConfirmClosing] = useState(false);
  // Mount at opacity 0, then flip to shown on the next frame so the open fades
  // in (CSS transition only fires on a value CHANGE, not the initial paint).
  const [switchConfirmShown, setSwitchConfirmShown] = useState(false);
  const closeSwitchConfirm = useCallback((then?: () => void) => {
    setSwitchConfirmClosing(true);
    window.setTimeout(() => {
      setSwitchToMonthlyConfirmOpen(false);
      setSwitchConfirmClosing(false);
      then?.();
    }, 240);
  }, []);
  useEffect(() => {
    if (switchToMonthlyConfirmOpen) {
      const id = requestAnimationFrame(() => setSwitchConfirmShown(true));
      return () => cancelAnimationFrame(id);
    }
    setSwitchConfirmShown(false);
    return undefined;
  }, [switchToMonthlyConfirmOpen]);
  const switchConfirmVisible = switchConfirmShown && !switchConfirmClosing;
  useEffect(() => { if (!open) { setSwitchToMonthlyConfirmOpen(false); setSwitchConfirmClosing(false); } }, [open]);
  // In-flight state for the Cancel Subscription POST. Drives the spinner
  // on the danger button + greys out both confirm buttons so the user
  // can't double-click while the page reloads.
  const [cancellingNow, setCancellingNow] = useState(false);
  useEffect(() => { if (!open) setCancellingNow(false); }, [open]);
  // Nonce bumped each time the inner SmoothLayout finishes a content swap
  // — included in the body-height effect deps so the modal recalculates
  // its target height after the cross-fade swaps to taller/shorter content.
  const [layoutSwapNonce, setLayoutSwapNonce] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentWrapRef = useRef<HTMLDivElement>(null);
  const prevHeightRef = useRef<number>(0);
  // Scroll affordance: show the "Scroll ⌄" fade while the body still has
  // content below the fold (phone/tablet — iOS paints no touch scrollbar).
  // [mounted] so it re-attaches once the body renders (the modal returns null
  // until mounted is true).
  const canScrollDown = useScrollAffordance(bodyRef, [mounted]);

  // Capture height before any state change that affects content
  const snapshotHeight = useCallback(() => {
    if (bodyRef.current) prevHeightRef.current = bodyRef.current.offsetHeight;
  }, []);

  /* Only the phone bounds the body to the viewport. Above 600px the card grows
     to its content and the OVERLAY scrolls instead (see ModalOverlay /
     ModalCard) — a long configuration then reads as one page rather than as a
     short pane with its own scrollbar inside a modal that has room to spare. */
  const boundToViewport = () => window.innerWidth <= 600;
  const viewportCap = (body: HTMLElement) => {
    const head = body.parentElement?.querySelector<HTMLElement>("[data-modal-head]")?.offsetHeight || 0;
    const footer = body.parentElement?.querySelector<HTMLElement>("[data-modal-footer]")?.offsetHeight || 0;
    return window.innerHeight - 48 - head - footer;
  };

  /* The height the body needs to show its content whole — measured by letting it
     lay itself out and reading the result, rather than adding up parts.
     Two things went wrong when this was assembled by hand from the wrapper's
     scrollHeight: `height` on a border-box element includes its padding (and
     CssBaseline makes everything border-box), and scrollHeight excludes the last
     child's bottom margin. Miss either and the body ends up a little shorter
     than its content, which shows up as a scrollbar on a modal that looks like
     it has room. The rect is the border-box height with the content at its
     natural size, which is exactly the number to set. */
  const measureContent = (body: HTMLElement) => {
    const prevHeight = body.style.height;
    const prevOverflow = body.style.overflowY;
    body.style.height = "auto";
    // Hidden while measuring: a scrollbar appearing mid-measure narrows the
    // content box, which reflows text taller and measures the wrong number.
    body.style.overflowY = "hidden";
    const needed = Math.ceil(body.getBoundingClientRect().height);
    body.style.height = prevHeight;
    body.style.overflowY = prevOverflow;
    return needed;
  };

  const setTab = useCallback((t: "config" | "plan") => { snapshotHeight(); setTabRaw(t); }, [snapshotHeight]);
  const setCancelConfirm = useCallback((v: boolean) => { snapshotHeight(); setCancelConfirmRaw(v); }, [snapshotHeight]);

  // Also snapshot before stepper changes
  const setNewChannelsAnimated = useCallback((v: number) => { snapshotHeight(); setNewChannels(v); }, [snapshotHeight]);
  const setNewClaimsAnimated = useCallback((v: number) => { snapshotHeight(); setNewClaims(v); }, [snapshotHeight]);

  useEffect(() => {
    if (open) { setTabRaw(initialTab ?? "config"); setCancelConfirmRaw(false); }
  }, [open, initialTab]);

  // After render, animate body height in either direction (grow or shrink).
  //
  // First render snaps to target (no prior height to animate from).
  // Subsequent renders: set body to `fromH` instantly, then rAF-schedule
  // the transition to `targetH`. CSS handles the animation between.
  //
  // Overflow stays `hidden` constantly — no toggling, so no scrollbar-gutter
  // or layout-width shifts on bump. Note: during a grow animation the new
  // (taller) content is briefly clipped at the bottom; the concurrent
  // SmoothLayout fade-in masks that visually.
  useEffect(() => {
    const body = bodyRef.current;
    const wrap = contentWrapRef.current;
    if (!body || !wrap || !mounted) return;

    const contentH = measureContent(body);
    const targetH = boundToViewport() ? Math.min(contentH, viewportCap(body)) : contentH;
    const fromH = prevHeightRef.current;
    // Only a body that was cut down to fit the viewport can need to scroll.
    // Where the height IS the content height, `hidden` both spares the modal a
    // scrollbar it has no use for and stops one flashing during a grow
    // animation, where the height is briefly smaller than the content.
    body.style.overflowY = targetH < contentH ? "auto" : "hidden";

    // First render / no change → snap.
    if (fromH === 0 || fromH === targetH) {
      body.style.transition = "none";
      body.style.height = `${targetH}px`;
      return;
    }

    // Grow or shrink → animate from fromH to targetH.
    body.style.transition = "none";
    body.style.height = `${fromH}px`;
    requestAnimationFrame(() => {
      body.style.transition = "height 0.32s cubic-bezier(0.16, 1, 0.3, 1)";
      body.style.height = `${targetH}px`;
    });
  }, [tab, cancelConfirm, newChannels, newClaims, mounted, previewAnnual, layoutSwapNonce]);

  // Recalculate on window resize
  useEffect(() => {
    if (!mounted) return;
    const handleResize = () => {
      const body = bodyRef.current;
      const wrap = contentWrapRef.current;
      if (!body || !wrap) return;
      const contentH = measureContent(body);
      const targetH = boundToViewport() ? Math.min(contentH, viewportCap(body)) : contentH;
      body.style.transition = "none";
      body.style.height = `${targetH}px`;
      // Same rule as the render pass: scroll only where the viewport cut the
      // body down. Resizing across the 600px line flips this either way.
      body.style.overflowY = targetH < contentH ? "auto" : "hidden";
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mounted]);

  if (!mounted) return null;

  return createPortal(
    <ModalOverlay>
      <ModalCard $closing={closing || !entered} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <ModalHead data-modal-head>
          <ModalClose onClick={handleClose}>&#x2715;</ModalClose>

          <ModalTitle>Manage Subscription</ModalTitle>

          {/* Full-width divider between the title row (which also carries the
              floating × close button) and the tabs. Negative horizontal margin
              breaks out past the head padding so the line spans the modal edge
              to edge — ModalCard's overflow:hidden clips it to the rounded
              corners. */}
          <div style={{ height: 1, background: "rgba(255, 255, 255, 0.1)", margin: "0 -32px" }} />

          {/* Tabs — the claims page's pills: gold tint, bright gold edge and a
              glow on the selected one, a quiet tint on the rest. Both labels are
              full white and one weight, so the row never re-flows when you
              switch; the edge and the glow carry the selection. */}
          <TabRow>
            {[
              { key: "config" as const, label: `${isAnnual ? "Annual" : "Monthly"} Configuration` },
              { key: "plan" as const, label: "Plan" },
            ].map((t) => {
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  style={{
                    height: 38,
                    boxSizing: "border-box",
                    padding: "0 18px",
                    borderRadius: 999,
                    cursor: "pointer",
                    lineHeight: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    fontFamily: fonts.body,
                    // `sm`, the scale's next step down from `body` — a compact
                    // control is what that size is for. Deliberately a token
                    // rather than a 14px in-between.
                    fontSize: fontSizes.sm,
                    fontWeight: 400,
                    color: colors.text.primary,
                    background: isActive ? "rgba(255, 220, 168, 0.18)" : "rgba(255, 255, 255, 0.05)",
                    border: isActive ? "1px solid rgba(255, 210, 155, 0.65)" : "1px solid rgba(186, 144, 92, 0.25)",
                    boxShadow: isActive ? "0 0 16px rgba(186, 144, 92, 0.28)" : "none",
                    transition: `all ${transitions.normal}`,
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.09)";
                      e.currentTarget.style.borderColor = "rgba(186, 144, 92, 0.45)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                      e.currentTarget.style.borderColor = "rgba(186, 144, 92, 0.25)";
                    }
                  }}
                >{t.label}</button>
              );
            })}
          </TabRow>
        </ModalHead>

        <ModalBody ref={bodyRef}>
        <div ref={contentWrapRef}>
        {/* ── Configuration Tab ── */}
        {tab === "config" && (
          <>
            {isPastDue && (
              <div style={{
                padding: "10px 14px",
                borderRadius: radius.md,
                background: "rgba(195, 75, 54, 0.18)",
                border: "1px solid rgba(195, 75, 54, 0.6)",
                fontFamily: fonts.body,
                fontSize: fontSizes.sm,
                fontWeight: 500,
                color: "#fff",
                lineHeight: 1.5,
                marginBottom: 18,
              }}>
                Update your payment method before continuing.
              </div>
            )}
            {isAwaitingReactivation && !isPastDue && (
              <div style={{
                padding: "12px 14px",
                borderRadius: radius.md,
                background: "rgba(191, 117, 22, 0.22)",
                border: "1px solid rgba(191, 117, 22, 0.75)",
                fontFamily: fonts.body,
                fontSize: fontSizes.sm,
                fontWeight: 500,
                color: "#fff",
                lineHeight: 1.5,
                marginBottom: 18,
              }}>
                Your billing cycle ended while your account was paused, so there is no running cycle to change.
                Reactivate to start a new one — you can pick a different configuration there.
                {onReactivateNow && (
                  <div style={{ marginTop: 10 }}>
                    <button
                      onClick={() => onReactivateNow()}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        padding: "8px 18px", borderRadius: 8,
                        background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
                        border: "none", color: "#2a1d14",
                        fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 600,
                        cursor: "pointer", whiteSpace: "nowrap",
                      }}
                    >
                      Reactivate
                    </button>
                  </div>
                )}
              </div>
            )}
            {isCancelling ? (
              <div style={{
                padding: "14px 18px",
                borderRadius: radius.xl,
                background: "rgba(231, 76, 60, 0.14)",
                border: "1px solid rgba(231, 76, 60, 0.7)",
                fontFamily: fonts.body,
                fontSize: fontSizes.body,
                color: "#fff",
                lineHeight: 1.55,
                marginBottom: 20,
              }}>
                Your subscription is scheduled to cancel on{" "}
                <span style={{ fontFamily: fonts.number, fontWeight: 600 }}>
                  {periodEndDate ? new Date(periodEndDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "your next billing date"}
                </span>
                {" "}—{" "}
                <button
                  type="button"
                  onClick={() => onReactivate?.()}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    color: colors.accent.blue,
                    fontFamily: fonts.body,
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    lineHeight: "inherit",
                    textDecoration: "none",
                    textUnderlineOffset: "3px",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                >
                  reactivate
                </button>
                {" "}it to change your limits.
              </div>
            ) : null}

            {/* Channels + Claims. Desktop ≥1150: one bordered box (the upper
                card's bottom edge doubles as the divider — joined via
                StepperGroup CSS). Phone/tablet ≤1149: two separate /features-
                style pills. Same render handles monthly and annual. */}
            <StepperGroup>
              <StepperCard>
                <StepperValue>
                  <span style={newChannels !== currentChannels ? { color: colors.accent.blue, fontWeight: 600 } : undefined}>{newChannels}</span>
                  {" "}YouTube {newChannels === 1 ? "channel" : "channels"}
                </StepperValue>
                <StepperControls>
                  <StepperBtn style={{ order: 0 }} disabled={isCancelling || configLocked} $hidden={newChannels <= subConfig.includedChannels} onClick={() => setNewChannelsAnimated(Math.max(subConfig.includedChannels, newChannels - 1))}><StepperGlyph>−</StepperGlyph></StepperBtn>
                  <StepperBtn style={{ order: 2 }} disabled={isCancelling || configLocked} onClick={() => setNewChannelsAnimated(newChannels + 1)}><StepperGlyph>+</StepperGlyph></StepperBtn>
                </StepperControls>
              </StepperCard>
              <StepperCard>
                <StepperValue>
                  <span style={newClaims !== currentClaims ? { color: colors.accent.blue, fontWeight: 600 } : undefined}>{newClaims}</span>
                  {" "}monthly claims{newChannels > 1 ? " per channel" : ""}
                </StepperValue>
                <StepperControls>
                  <StepperBtn style={{ order: 0 }} disabled={isCancelling || configLocked} $hidden={newClaims <= subConfig.includedClaims} onClick={() => setNewClaimsAnimated(Math.max(subConfig.includedClaims, newClaims - subConfig.claimIncrement))}><StepperGlyph>−</StepperGlyph></StepperBtn>
                  <StepperBtn style={{ order: 2 }} disabled={isCancelling || configLocked} onClick={() => setNewClaimsAnimated(Math.min(200, newClaims + subConfig.claimIncrement))}><StepperGlyph>+</StepperGlyph></StepperBtn>
                </StepperControls>
              </StepperCard>
            </StepperGroup>

            {(() => {
              const paymentLabel = hasChanges
                ? (isAnnual ? "New Annual Payment" : "New Monthly Payment")
                : (isAnnual ? "Current Annual Payment" : "Current Monthly Payment");
              const layoutVersion = !hasChanges
                ? "current"
                : isDowngrade
                  ? "downgrade"
                  : previewingSwitch
                    ? "switch"
                    : isAnnual
                      ? "upgrade-annual"
                      : "upgrade-monthly";

              // Compute upgrade math once for whichever variant ends up rendering.
              const cashCredit = Math.round(oldTotal * (Math.max(0, effectiveDaysLeft) / Math.max(1, cycleLengthDays ?? 30)) * 100) / 100;
              const monthlyChargeToday = Math.max(0, Math.round((newTotal - cashCredit) * 100) / 100);
              const monthlyNextRenewalDate = (() => {
                const d = new Date();
                d.setDate(d.getDate() + 30);
                return d;
              })();
              const switchAnnualTotal = Math.round(newTotal * 12 * 100) / 100;
              const yearlyDiff = (newTotal - oldTotal) * 12;
              const annualProratedDiff = Math.round(yearlyDiff * (Math.max(0, effectiveDaysLeft) / 365) * 100) / 100;
              const annualNewYearly = Math.round(newTotal * 12 * 100) / 100;
              const formatShort = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

              return (
                <>
                  <SectionLabel><SmoothLabel text={paymentLabel} /></SectionLabel>

                  {/* Annual preview. It belongs to the payment, not to the
                      limits, so it sits under the payment heading and directly
                      above the amount it changes — it used to live above the
                      channels/claims steppers, where it read as a third limit
                      control. Local state only: the subscription stays monthly
                      until the Upgrade button at the foot of the modal is
                      pressed, which is what dispatches the switch-to-annual
                      checkout. It stays visible while previewing so the choice
                      can be undone. */}
                  {!isAnnualSub && !isCancelling && !configLocked && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
                      <BillingSwitch
                        $annual={previewAnnual}
                        onClick={() => setPreviewAnnual(!previewAnnual)}
                      />
                      <BillingLabel
                        $active={previewAnnual}
                        onClick={() => setPreviewAnnual(!previewAnnual)}
                      >
                        Annual (Save {subConfig.annualDiscountPercent}%)
                      </BillingLabel>
                    </div>
                  )}

                  <SmoothLayout version={layoutVersion} onSwap={() => { snapshotHeight(); setLayoutSwapNonce(n => n + 1); }}>
                    {/* Current / Downgrade — neutral box, no charge today. */}
                    {(!hasChanges || isDowngrade) && (
                      <div style={{
                        position: "relative",
                        marginBottom: 24,
                        padding: "16px 18px 18px",
                        background: "rgba(255, 255, 255, 0.04)",
                        border: "1px solid rgba(235, 194, 142, 0.25)",
                        borderRadius: radius.xl,
                        textAlign: "center",
                        fontFamily: fonts.number,
                        fontSize: "1.125rem",
                        fontWeight: 700,
                        // Current cost (no changes) → white; downgrade "new"
                        // rate → blue accent.
                        color: !hasChanges ? "#fff" : colors.accent.blue,
                        overflow: "hidden",
                      }}>
                        {isAnnual ? <>${(Math.round(newTotal * 12 * 100) / 100).toFixed(2)}/year <span style={{ fontSize: "0.85em", fontWeight: 400, color: "#fff" }}>(${newTotal.toFixed(2)}/mo)</span></> : `$${newTotal.toFixed(2)}/mo`}
                        {isAnnual && (
                          <span style={{
                            position: "absolute",
                            right: 14,
                            top: "50%",
                            transform: "translateY(-50%)",
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "3px 10px",
                            borderRadius: 999,
                            background: colors.tier.free.bg,
                            color: colors.tier.free.text,
                            fontFamily: fonts.number,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            whiteSpace: "nowrap",
                          }}>
                            −{subConfig.annualDiscountPercent}% saved
                          </span>
                        )}
                      </div>
                    )}

                    {/* Monthly upgrade — cycle reset. */}
                    {hasChanges && isUpgrade && !isAnnual && (
                      <div style={{
                        background: "rgba(156, 211, 253, 0.08)",
                        border: "1px solid rgba(156, 211, 253, 0.4)",
                        borderRadius: radius.xl,
                        padding: "16px 18px",
                        marginBottom: 16,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ fontFamily: fonts.number, fontSize: "0.75rem", color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                            From {formatShort(monthlyNextRenewalDate)}
                          </div>
                          <div style={{ fontFamily: fonts.number, fontSize: "1.75rem", fontWeight: 700, color: "#fff", lineHeight: 1, whiteSpace: "nowrap" }}>
                            ${newTotal.toFixed(2)}
                          </div>
                        </div>
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(156, 211, 253, 0.2)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ fontFamily: fonts.number, fontSize: "0.75rem", color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                            Pay Today (Upgrade current month)
                          </div>
                          <div style={{ fontFamily: fonts.number, fontSize: "1.75rem", fontWeight: 700, color: "#9cd3fd", lineHeight: 1, whiteSpace: "nowrap" }}>
                            ${monthlyChargeToday.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Switch-to-Annual preview (monthly user toggled the switch). */}
                    {hasChanges && previewingSwitch && (
                      <div style={{
                        background: "rgba(156, 211, 253, 0.08)",
                        border: "1px solid rgba(156, 211, 253, 0.4)",
                        borderRadius: radius.xl,
                        padding: "16px 18px",
                        marginBottom: 16,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ fontFamily: fonts.number, fontSize: "0.75rem", color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                            Monthly Payment ({subConfig.annualDiscountPercent}% saved)
                          </div>
                          <div style={{ fontFamily: fonts.number, fontSize: "1.75rem", fontWeight: 700, color: "#fff", lineHeight: 1, whiteSpace: "nowrap" }}>
                            ${newTotal.toFixed(2)}
                          </div>
                        </div>
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(156, 211, 253, 0.2)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ fontFamily: fonts.number, fontSize: "0.75rem", color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                            Pay Today (Annual Start)
                          </div>
                          <div style={{ fontFamily: fonts.number, fontSize: "1.75rem", fontWeight: 700, color: "#9cd3fd", lineHeight: 1, whiteSpace: "nowrap" }}>
                            ${switchAnnualTotal.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Annual upgrade — prorated rest-of-year. */}
                    {hasChanges && isUpgrade && isAnnual && !previewingSwitch && (
                      <div style={{
                        background: "rgba(156, 211, 253, 0.08)",
                        border: "1px solid rgba(156, 211, 253, 0.4)",
                        borderRadius: radius.xl,
                        padding: "16px 18px",
                        marginBottom: 16,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ fontFamily: fonts.number, fontSize: "0.75rem", color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                            From {periodEndDate ? formatShort(new Date(periodEndDate)) : "next renewal"}
                          </div>
                          <div style={{ fontFamily: fonts.number, fontSize: "1.75rem", fontWeight: 700, color: "#fff", lineHeight: 1, whiteSpace: "nowrap" }}>
                            ${annualNewYearly.toFixed(2)}
                          </div>
                        </div>
                        <div style={{ marginTop: 6, textAlign: "right", fontFamily: fonts.number, fontSize: "1rem", fontWeight: 600, color: "rgba(251, 244, 236, 0.7)" }}>
                          = ${newTotal.toFixed(2)}/mo
                        </div>
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(156, 211, 253, 0.2)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ fontFamily: fonts.number, fontSize: "0.75rem", color: "#9cd3fd", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                            Pay Today (Upgrade current year)
                          </div>
                          <div style={{ fontFamily: fonts.number, fontSize: "1.75rem", fontWeight: 700, color: "#9cd3fd", lineHeight: 1, whiteSpace: "nowrap" }}>
                            ${annualProratedDiff.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    )}
                  </SmoothLayout>
                </>
              );
            })()}


          </>
        )}

        {/* ── Plan Tab ── */}
        {tab === "plan" && (() => {
          const endDate = periodEndDate ? new Date(periodEndDate) : null;
          const formattedEnd = endDate ? endDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "the end of your billing period";
          const isAnnualPlan = billingPeriod === "annual";

          const iconStyle: React.CSSProperties = { width: 18, height: 18, fill: "none", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round", flexShrink: 0 };
          const loseIcon = { ...iconStyle, stroke: "rgb(255, 149, 133)" };
          const keepIcon = { ...iconStyle, stroke: "rgb(84, 255, 193)" };

          const loseItems = [
            { icon: <svg viewBox="0 0 24 24" style={loseIcon}><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>, text: "Access to Premium tracks" },
            { icon: <svg viewBox="0 0 24 24" style={loseIcon}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></svg>, text: "WAV format downloads" },
            { icon: <svg viewBox="0 0 24 24" style={loseIcon}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>, text: "Automatic claim resolving" },
            { icon: <svg viewBox="0 0 24 24" style={loseIcon}><path d="M23 7l-7 5 7 5V7z" /><path d="M14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z" /></svg>, text: "Registered YouTube channels" },
          ];

          const keepItems = [
            { icon: <svg viewBox="0 0 24 24" style={keepIcon}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>, text: "Your account" },
            { icon: <svg viewBox="0 0 24 24" style={keepIcon}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M16 13H8" /><path d="M16 17H8" /></svg>, text: "YouTube video licenses you purchased" },
            { icon: <svg viewBox="0 0 24 24" style={keepIcon}><path d="M20 6L9 17l-5-5" /></svg>, text: "Previously resolved claims" },
            { icon: <svg viewBox="0 0 24 24" style={keepIcon}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>, text: "Free plan access with credit text" },
          ];

          const hasPendingSwitch = isAnnualPlan && pendingBillingPeriod === "monthly";
          return (
            <>
              {/* Billing-period card. Annual users get a "Switch to Monthly"
                  option; monthly users get a "Switch to Annual (Save N%)"
                  shortcut that jumps to the Configuration tab with the annual
                  toggle pre-activated (where the switch is actually confirmed). */}
              {isAnnualPlan && (
                <>
              <div style={{ fontFamily: fonts.body, fontSize: "1.125rem", fontWeight: 500, color: colors.text.heading, marginBottom: 12 }}>Billing Period</div>
              <div style={{
                padding: "16px 18px",
                borderRadius: radius.xl,
                background: hasPendingSwitch ? "rgba(245, 166, 35, 0.12)" : "rgba(255, 255, 255, 0.05)",
                border: hasPendingSwitch ? "1px solid rgba(245, 166, 35, 0.5)" : "1px solid rgba(255, 255, 255, 0.12)",
                fontFamily: fonts.body,
                fontSize: fontSizes.body,
                color: "#fff",
                lineHeight: 1.6,
                marginBottom: 28,
              }}>
                <div style={{ marginBottom: 14 }}>
                  {hasPendingSwitch ? (
                    <>You're billed <strong>annually</strong> through <strong style={{ fontFamily: fonts.number }}>{formattedEnd}</strong>. After that, your subscription will switch to <strong>monthly billing</strong> as scheduled.</>
                  ) : (
                    <>You're billed annually. Next renewal: <span style={{ fontFamily: fonts.number }}>{formattedEnd}</span>.</>
                  )}
                </div>
                <CTAButton
                  $variant="secondary"
                  disabled={configLocked}
                  title={configLocked ? (isAwaitingReactivation ? "Reactivate to start a new billing cycle — you can change your plan there" : "Update your payment method to change your billing period") : undefined}
                  style={configLocked ? {
                    // past_due: billing-period changes are blocked — only cancel
                    // is allowed. Grey it out.
                    background: "rgba(255, 255, 255, 0.04)",
                    borderColor: "rgba(255, 255, 255, 0.1)",
                    color: "rgba(251, 244, 236, 0.3)",
                    fontWeight: 500,
                    cursor: "not-allowed",
                  } : {
                    // Outlined "subtle" treatment — matches the Cancel
                    // Subscription button style. Billing-period change is
                    // a low-key destructive action (you're losing the
                    // annual discount), so present it as a quiet outline
                    // button rather than a loud accent.
                    background: "transparent",
                    borderColor: "rgba(255, 255, 255, 0.18)",
                    color: "rgba(255, 255, 255, 0.7)",
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) => {
                    if (configLocked) return;
                    (e.currentTarget as HTMLElement).style.background = "rgba(255, 255, 255, 0.04)";
                    (e.currentTarget as HTMLElement).style.borderColor = "rgba(255, 255, 255, 0.32)";
                    (e.currentTarget as HTMLElement).style.color = "rgba(255, 255, 255, 0.95)";
                  }}
                  onMouseLeave={(e) => {
                    if (configLocked) return;
                    (e.currentTarget as HTMLElement).style.background = "transparent";
                    (e.currentTarget as HTMLElement).style.borderColor = "rgba(255, 255, 255, 0.18)";
                    (e.currentTarget as HTMLElement).style.color = "rgba(255, 255, 255, 0.7)";
                  }}
                  onClick={() => {
                    if (configLocked) return;
                    if (hasPendingSwitch) {
                      handleClose();
                      onCancelBillingPeriodSwitch?.();
                    } else if (isAnnualPlan) {
                      // Surface the savings-lost + effective-date warning
                      // before firing the actual switch.
                      setSwitchToMonthlyConfirmOpen(true);
                    } else {
                      handleClose();
                      dispatch(openSwitchToAnnualCheckout({
                        channels: currentChannels,
                        claims: currentClaims,
                      }));
                    }
                  }}
                >
                  {hasPendingSwitch
                    ? "Keep Annual"
                    : "Switch to Monthly Payment"}
                </CTAButton>
              </div>
                </>
              )}

              {/* Monthly users: a "Switch to Annual (Save N%)" shortcut. It
                  doesn't switch directly — it jumps to the Configuration tab
                  with the annual toggle on, where the new annual price shows
                  and the bottom button confirms the switch-to-annual checkout. */}
              {!isAnnualPlan && !isCancelling && (
                <>
                  <div style={{ fontFamily: fonts.body, fontSize: "1.125rem", fontWeight: 500, color: colors.text.heading, marginBottom: 12 }}>Billing Period</div>
                  <div style={{
                    padding: "16px 18px",
                    borderRadius: radius.xl,
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    fontFamily: fonts.body,
                    fontSize: fontSizes.body,
                    color: "#fff",
                    lineHeight: 1.6,
                    marginBottom: 28,
                  }}>
                    <div style={{ marginBottom: 14 }}>
                      You're billed monthly. Next renewal: <span style={{ fontFamily: fonts.number }}>{formattedEnd}</span>.
                    </div>
                    <CTAButton
                      $variant="secondary"
                      disabled={configLocked}
                      title={configLocked ? (isAwaitingReactivation ? "Reactivate to start a new billing cycle — you can change your plan there" : "Update your payment method to change your billing period") : undefined}
                      style={configLocked ? {
                        background: "rgba(255, 255, 255, 0.04)",
                        borderColor: "rgba(255, 255, 255, 0.1)",
                        color: "rgba(251, 244, 236, 0.3)",
                        fontWeight: 600,
                        cursor: "not-allowed",
                      } : {
                        // Positive money-saving action → green accent (vs the
                        // quiet outline used for "Switch to Monthly").
                        background: "rgba(52, 211, 153, 0.12)",
                        borderColor: "rgba(52, 211, 153, 0.5)",
                        color: "rgb(84, 255, 193)",
                        fontWeight: 600,
                      }}
                      onMouseEnter={(e) => {
                        if (configLocked) return;
                        (e.currentTarget as HTMLElement).style.background = "rgba(52, 211, 153, 0.2)";
                        (e.currentTarget as HTMLElement).style.borderColor = "rgba(52, 211, 153, 0.7)";
                      }}
                      onMouseLeave={(e) => {
                        if (configLocked) return;
                        (e.currentTarget as HTMLElement).style.background = "rgba(52, 211, 153, 0.12)";
                        (e.currentTarget as HTMLElement).style.borderColor = "rgba(52, 211, 153, 0.5)";
                      }}
                      onClick={() => {
                        if (configLocked) return;
                        // Reset any in-progress channel/claim edits back to the
                        // user's CURRENT subscription — "Switch to Annual" is a
                        // billing-cadence change of the existing plan, not an
                        // upgrade, so the config tab should show the current plan
                        // (just in annual mode), never the half-made new selection.
                        setNewChannels(currentChannels);
                        setNewClaims(currentClaims);
                        setPreviewAnnual(true);
                        setTab("config");
                      }}
                    >
                      Switch to Annual (Save {subConfig.annualDiscountPercent}%)
                    </CTAButton>
                  </div>
                </>
              )}

              <div style={{ fontFamily: fonts.body, fontSize: "1.125rem", fontWeight: 500, color: colors.text.heading, marginBottom: 12 }}>
                {cancelConfirm ? "Cancel Subscription" : "Manage Plan"}
              </div>
              {/* Cancel card — collapsed by default to just the trigger button.
                  Clicking it expands the card to show full context (copy +
                  lose/keep lists + final confirm + back-out). */}
              <div style={{
                padding: "16px 18px",
                borderRadius: radius.xl,
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                fontFamily: fonts.body,
                fontSize: fontSizes.body,
                color: "#fff",
                lineHeight: 1.6,
                marginBottom: 0,
              }}>
                {!cancelConfirm ? (
                  <CancelLink type="button" onClick={() => setCancelConfirm(true)}>
                    Cancel Subscription
                  </CancelLink>
                ) : (
                  <>
                    <div style={{
                      padding: "14px 18px",
                      borderRadius: radius.xl,
                      background: "rgba(231, 76, 60, 0.14)",
                      border: "1px solid rgba(231, 76, 60, 0.7)",
                      color: "#fff",
                      lineHeight: 1.55,
                      marginBottom: 26,
                    }}>
                      {isAnnualPlan ? (
                        <>Your annual plan is paid through <strong style={{ fontFamily: fonts.number }}>{formattedEnd}</strong>, so you'll keep subscription access until then. After that, your account will be downgraded to the Free plan. Cancelling does not delete your account.{hasPendingSwitch ? <> Your scheduled switch to monthly will also be removed.</> : null}</>
                      ) : (
                        <>Your subscription renews monthly. If you cancel, you will keep subscription access until <strong style={{ fontFamily: fonts.number }}>{formattedEnd}</strong>. After that, your account will be downgraded to the Free plan. Cancelling does not delete your account.</>
                      )}
                    </div>

                    {/* What you'll lose */}
                    <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "0 0 14px" }}>
                      <div style={{ flex: 1, height: 1, background: "rgba(235, 194, 142, 0.2)" }} />
                      <span style={{ color: "#fff", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "2px", whiteSpace: "nowrap" }}>You will lose</span>
                      <div style={{ flex: 1, height: 1, background: "rgba(235, 194, 142, 0.2)" }} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 26 }}>
                      {loseItems.map((item, i) => (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", gap: 12,
                          padding: "2px 0",
                          fontFamily: fonts.body, fontSize: fontSizes.body, color: "#fff", lineHeight: 1.35,
                        }}>
                          {item.icon}
                          {item.text}
                        </div>
                      ))}
                    </div>

                    {/* What you'll keep */}
                    <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "0 0 14px" }}>
                      <div style={{ flex: 1, height: 1, background: "rgba(235, 194, 142, 0.2)" }} />
                      <span style={{ color: "#fff", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "2px", whiteSpace: "nowrap" }}>You will keep</span>
                      <div style={{ flex: 1, height: 1, background: "rgba(235, 194, 142, 0.2)" }} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 26 }}>
                      {keepItems.map((item, i) => (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", gap: 12,
                          padding: "2px 0",
                          fontFamily: fonts.body, fontSize: fontSizes.body, color: "#fff", lineHeight: 1.35,
                        }}>
                          {item.icon}
                          {item.text}
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", gap: 10 }}>
                        <CTAButton
                          $variant="danger"
                          style={{
                            flex: 1,
                            opacity: cancellingNow ? 0.55 : 1,
                            cursor: cancellingNow ? "not-allowed" : "pointer",
                            pointerEvents: cancellingNow ? "none" : "auto",
                          }}
                          onClick={async () => {
                            if (cancellingNow) return;
                            setCancellingNow(true);
                            try {
                              await instance.post("/stripe/subscriptions/cancel");
                              window.location.reload();
                            } catch {
                              setCancellingNow(false);
                              /* TODO: show error */
                            }
                          }}
                        >
                          {cancellingNow ? (
                            <><InlineSpinner />Cancelling…</>
                          ) : (
                            "Cancel Subscription"
                          )}
                        </CTAButton>
                        <CTAButton
                          $variant="secondary"
                          style={{
                            flex: 1,
                            // Override the gold "secondary" treatment with green
                            // for the positive "Keep Subscription" action.
                            background: "rgba(52, 211, 153, 0.85)",
                            borderColor: "rgba(52, 211, 153, 0.85)",
                            color: "#fff",
                            opacity: cancellingNow ? 0.55 : 1,
                            cursor: cancellingNow ? "not-allowed" : "pointer",
                            pointerEvents: cancellingNow ? "none" : "auto",
                          }}
                          onMouseEnter={(e) => {
                            if (cancellingNow) return;
                            (e.currentTarget as HTMLElement).style.background = "rgba(78, 224, 170, 0.95)";
                            (e.currentTarget as HTMLElement).style.borderColor = "rgba(78, 224, 170, 0.95)";
                          }}
                          onMouseLeave={(e) => {
                            if (cancellingNow) return;
                            (e.currentTarget as HTMLElement).style.background = "rgba(52, 211, 153, 0.85)";
                            (e.currentTarget as HTMLElement).style.borderColor = "rgba(52, 211, 153, 0.85)";
                          }}
                          onClick={() => { if (!cancellingNow) setCancelConfirm(false); }}
                        >
                          Keep Subscription
                        </CTAButton>
                      </div>
                    </div>
                  </>
                )}
              </div>


            </>
          );
        })()}
        </div>
        </ModalBody>

        {/* ── Fixed Footer ──
            Plan tab has no footer content — collapse padding so the bottom
            space matches the modal's normal edge spacing instead of stacking
            both body padding-bottom + footer padding. Configuration tab keeps
            the original padding for its alerts + CTA. */}
        <ModalFooter style={tab === "plan" ? { padding: "0 32px 32px" } : undefined}>
          <ScrollFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></ScrollFade>
          {tab === "config" && (
            <>
              {hasChanges && !configLocked && <CTAButton
                onClick={async () => {
                  // Previewing a monthly → annual switch: any limit changes
                  // are baked into the new annual sub via the standard
                  // switch-to-annual checkout, so we send the freshly
                  // adjusted channels/claims.
                  if (previewingSwitch) {
                    handleClose();
                    dispatch(openSwitchToAnnualCheckout({
                      channels: newChannels,
                      claims: newClaims,
                    }));
                    return;
                  }
                  if (isUpgrade) {
                    handleClose();
                    dispatch(openSubscriptionUpgrade({
                      oldChannels: currentChannels,
                      oldClaims: currentClaims,
                      newChannels,
                      newClaims,
                      billingPeriod,
                      oldTotal,
                      newTotal,
                      proratedAmount: 0,
                      daysLeftInPeriod: effectiveDaysLeft,
                      cycleLengthDays,
                      periodEndDate,
                    }));
                  } else {
                    // Downgrade — confirm in place. No prorated charge, no
                    // cart slide-out; the new config applies at next renewal.
                    handleClose();
                    onScheduleDowngrade?.(newChannels, newClaims);
                  }
                }}
              >
                {(!previewingSwitch && isDowngrade)
                  ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M12 5v14" /><path d="M19 12l-7 7-7-7" /></svg>
                  : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></svg>}
                {previewingSwitch ? "Switch to Annual" : isDowngrade ? "Schedule Downgrade" : "Upgrade"}
              </CTAButton>}
              {/* Idle placeholder — when no changes are pending, render a
                  disabled "Upgrade" CTA so the user sees that the action
                  exists. Becomes the live Upgrade/Downgrade button as soon
                  as channels or claims are bumped. */}
              {(!hasChanges || configLocked) && (
                <CTAButton style={{ opacity: 0.4, cursor: "not-allowed" }} onClick={() => {}}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></svg>
                  Upgrade
                </CTAButton>
              )}
            </>
          )}
        </ModalFooter>
      </ModalCard>
      {/* Switch-to-Monthly confirmation overlay. Surfaces the annual
          discount the user is about to give up + the effective date
          (their current annual period end). Sits above the Manage modal
          card so the user can read context without dismissing the modal. */}
      {switchToMonthlyConfirmOpen && (
        <div
          onClick={() => closeSwitchConfirm()}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0, 0, 0, 0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10001,
            padding: 16,
            opacity: switchConfirmVisible ? 1 : 0,
            transition: "opacity 240ms ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 540, width: "100%",
              // Match the Manage modal's gradient surface so the confirm
              // reads as part of the same modal family rather than a flat
              // popover on top.
              background: `
                radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
                radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
                radial-gradient(ellipse 70% 35% at 50% 8%, rgba(150, 118, 82, 0.4) 0%, transparent 70%),
                linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%)
              `,
              border: "none",
              borderRadius: 24,
              padding: "28px 28px 24px",
              color: "#fff",
              boxShadow: "0 24px 64px rgba(0, 0, 0, 0.5)",
              opacity: switchConfirmVisible ? 1 : 0,
              transform: switchConfirmVisible ? "none" : "translateY(8px) scale(0.98)",
              transition: "opacity 240ms ease, transform 240ms ease",
            }}
          >
            <div style={{
              fontFamily: fonts.heading, fontSize: "1.5rem", fontWeight: 600,
              marginBottom: 16, color: colors.text.heading,
            }}>
              Switch to monthly billing?
            </div>
            <div style={{
              height: 1, background: "rgba(235, 194, 142, 0.18)",
              margin: "0 -28px 20px",
            }} />
            <div style={{
              fontFamily: fonts.body, fontSize: fontSizes.body, lineHeight: 1.55,
              color: "rgba(255, 255, 255, 0.85)", marginBottom: 16,
            }}>
              You're about to give up your <strong style={{ color: "#fff", fontFamily: fonts.number, fontWeight: 600 }}>{subConfig.annualDiscountPercent}% annual discount</strong>.
            </div>
            <div style={{
              fontFamily: fonts.body, fontSize: fontSizes.body, lineHeight: 1.55,
              color: "rgba(255, 255, 255, 0.85)", marginBottom: 24,
              padding: "12px 14px", borderRadius: radius.md,
              background: "rgba(245, 166, 35, 0.08)",
              border: "1px solid rgba(245, 166, 35, 0.3)",
            }}>
              The change takes effect on{" "}
              <strong style={{ color: "#fff", fontFamily: fonts.number, fontWeight: 600 }}>
                {periodEndDate ? new Date(periodEndDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "your next renewal"}
              </strong>
              . Until then, you keep your current annual benefits.
            </div>
            <div style={{ display: "flex", gap: 12, paddingTop: 12 }}>
              <button
                onClick={() => closeSwitchConfirm()}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 24px rgba(52, 211, 153, 0.45)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 0 16px rgba(52, 211, 153, 0.22)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                style={{
                  flex: 1,
                  padding: "14px 28px", borderRadius: radius.md,
                  background: "linear-gradient(135deg, #34d399 0%, #6ee7b7 100%)",
                  border: "none", color: "#06281c",
                  fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 600,
                  cursor: "pointer", boxShadow: "0 0 16px rgba(52, 211, 153, 0.22)",
                  transition: `all ${transitions.normal}`,
                }}
              >
                Keep Annual
              </button>
              <button
                onClick={() => closeSwitchConfirm(() => { handleClose(); onSwitchToMonthly?.(); })}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 24px rgba(186, 144, 92, 0.5)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 0 16px rgba(186, 144, 92, 0.25)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                style={{
                  flex: 1,
                  padding: "14px 28px", borderRadius: radius.md,
                  background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
                  border: "none", color: colors.brown.darkest,
                  fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 600,
                  cursor: "pointer", boxShadow: "0 0 16px rgba(186, 144, 92, 0.25)",
                  transition: `all ${transitions.normal}`,
                }}
              >
                Switch to Monthly
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalOverlay>,
    document.body,
  );
};

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */

const AccountSubscription: React.FC = () => {
  const { accountType } = useAccountType();
  // Tighter heading→box gap on phone/tablet (desktop keeps the roomier spacing).
  const isCompact = useMediaQuery("(max-width: 1149.98px)");
  const dispatch = useAppDispatch();
  const subConfig = useSubscriptionConfig();

  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const [channels, setChannels] = useState(subConfig.includedChannels);
  const [claims, setClaims] = useState(subConfig.includedClaims);

  useEffect(() => {
    setChannels(subConfig.includedChannels);
    setClaims(subConfig.includedClaims);
  }, [subConfig]);

  const isAnnual = billing === "annual";
  const previewPricing = computeSubscriptionPricing(subConfig, { channels, claims, isAnnual });
  const premiumBase = previewPricing.baseMonthly;
  const extraChannels = previewPricing.extraChannels;
  const extraClaimPacks = previewPricing.extraClaimPacks;
  const addonsPrice = previewPricing.addonsMonthly; // claims billed per channel
  const monthlyTotal = previewPricing.totalMonthly;

  const isFree = accountType === AccountType.FREE;
  const isSubscribed = accountType === AccountType.SUBSCRIPTION;
  const [manageOpen, setManageOpen] = useState(false);
  // Which tab the Manage Subscription modal opens on. past_due opens straight to
  // the Plan/Cancel tab (config changes are blocked until payment is fixed).
  const [manageInitialTab, setManageInitialTab] = useState<"config" | "plan">("config");

  // A2 — Music Creators have no subscription to manage; this page is a blank
  // shell for them (the sidebar tab is already hidden). Bounce them to the
  // account home. Positive match on MUSIC_CREATOR, so it never fires during
  // the brief FREE-default load window before accountType resolves.
  const navigate = useNavigate();
  useEffect(() => {
    if (accountType === AccountType.MUSIC_CREATOR) {
      navigate(ERoutes.Profile, { replace: true });
    }
  }, [accountType, navigate]);

  // UpgradeReviewView fires this event when the user clicks Back in the cart
  // slide-out so we can reopen the manage subscription modal they came from.
  useEffect(() => {
    const reopen = () => setManageOpen(true);
    window.addEventListener("reopen-manage-subscription", reopen);
    return () => window.removeEventListener("reopen-manage-subscription", reopen);
  }, []);

  // Deep-link from emails / external CTAs: `?manage=1` (or `?manage=open`)
  // opens the Manage Subscription modal on mount. The "Upgrade Now" CTA in
  // the monthly_claim_limit_reached email points here so the user lands
  // directly on the configuration step. Strip the param after we read it
  // so a reload or share-link doesn't re-open the modal forever.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const flag = searchParams.get("manage");
    // Only subscribers have a Manage Subscription modal — a ?manage deep-link
    // must NOT pop the modal over mock data for a free/creator user (L4).
    // Depend on isSubscribed so a real subscriber whose plan resolves AFTER
    // mount still opens the modal once it flips true.
    if (flag && flag !== "0" && flag !== "false" && isSubscribed) {
      // ?manage=plan|cancel lands on the Plan tab (where cancel + billing-period
      // live) — used by the credit-pause reactivation modal's "Cancel" link.
      if (flag === "plan" || flag === "cancel") setManageInitialTab("plan");
      setManageOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("manage");
      setSearchParams(next, { replace: true });
    }
  }, [isSubscribed]);
  const status = STATUS_MAP[accountType] || STATUS_MAP.FREE;

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // DEV: free user "previously subscribed" toggle. The real signal is
  // `cancelledSubId` (set further down once /subscriptions/my/history
  // resolves) — the dev toggle is the fallback for previewing the
  // Welcome-Back state without a real cancelled sub in the DB.
  const [devWasSubscribed, setDevWasSubscribed] = useState(false);

  // Green success banner shown above the page title after Reactivate or
  // Keep Current Plan — mirrors the banner on /account/claims.
  // Persisted in localStorage per user so the message survives reloads +
  // logout/login and only goes away when the user clicks ×.
  const { myData } = useAuth();
  const successAlertUserId = (myData as any)?.id ?? (myData as any)?._id ?? null;
  const [successAlert, setSuccessAlert] = useState<string | null>(() => readSubscriptionAlert(successAlertUserId));
  // Re-read once `myData` resolves (may be null on first render).
  useEffect(() => {
    if (!successAlertUserId) return;
    const persistent = readSubscriptionAlert(successAlertUserId);
    if (persistent && persistent !== successAlert) setSuccessAlert(persistent);
  }, [successAlertUserId]);
  // Dismiss-out animation state — when true, the banner fades + collapses
  // before the underlying `successAlert` is set to null. Mirrors the fade
  // pattern used elsewhere on the page (SmoothLabel / SmoothLayout).
  const [closingAlert, setClosingAlert] = useState(false);
  const dismissSuccessAlert = useCallback(() => {
    setClosingAlert(true);
    window.setTimeout(() => {
      setSuccessAlert(null);
      clearSubscriptionAlert(successAlertUserId);
      setClosingAlert(false);
    }, 260);
  }, [successAlertUserId]);
  // (Fade-in for the green success banner is handled by always rendering
  // the banner div and letting CSS transition opacity 0→1 when
  // successAlert flips from null→msg. See the banner JSX below.)
  // Fade-out state for the red Cancelling banner — driven by the Keep
  // Subscription click so the banner can collapse smoothly before the
  // success banner appears.
  const [cancellingFadingOut, setCancellingFadingOut] = useState(false);
  // Fade-in state for the red Cancelling banner — mirrors `enteringAlert`
  // for the green banner. Triggers when isCancelling transitions
  // false → true mid-session.
  const [enteringCancelBanner, setEnteringCancelBanner] = useState(false);
  // Fade-out state for the gold pending-downgrade banner. Set by the
  // inline Keep Current Plan handler so the yellow collapses smoothly
  // before the green success alert slides in.
  const [closingDowngradeBanner, setClosingDowngradeBanner] = useState(false);

  // DEV: subscription state switcher
  // past_due is mirrored via sessionStorage so /account also shows the
  // past_due banner + red payment-section highlight when this page sets it.
  type DevSubState = "active" | "cancelling" | "past_due" | "pending_downgrade" | "pending_switch_to_monthly";
  const [devSubState, setDevSubStateLocal] = useState<DevSubState>(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem("dev-past-due") === "1") return "past_due";
    return "active";
  });
  const setDevSubState = (s: DevSubState) => {
    setDevSubStateLocal(s);
    if (typeof window !== "undefined") {
      if (s === "past_due") sessionStorage.setItem("dev-past-due", "1");
      else sessionStorage.removeItem("dev-past-due");
    }
  };
  const isPendingDowngrade = devSubState === "pending_downgrade";

  // DEV: billing period — set globally via the header DevAccountSwitcher.
  // Mirror it into local state and listen for the same-tab change event so
  // the page re-renders with annual-vs-monthly variants instantly.
  const [devBillingPeriod, setDevBillingPeriodLocal] = useState<"monthly" | "annual">(readDevBillingPeriod);
  useEffect(() => {
    const onChange = () => setDevBillingPeriodLocal(readDevBillingPeriod());
    window.addEventListener("dev-billing-period-change", onChange);
    return () => window.removeEventListener("dev-billing-period-change", onChange);
  }, []);

  // Real subscription data from `GET /subscriptions/my`. Falls back to a
  // demo shape so the page still renders during the initial fetch and for
  // logged-out / no-sub previews.
  const [realSub, setRealSub] = useState<any | null>(null);
  const [realChannelCredits, setRealChannelCredits] = useState<Array<{ channelName: string | null; youtubeChannelId: string; used: number; limit: number }>>([]);
  // Most-recent CANCELLED sub id (when no active/past-due sub) — drives
  // the in-page red "subscription cancelled" banner below. Dismissed via
  // the shared per-sub flag `cancelled-banner-dismissed-<subId>` — the SAME
  // key the website-wide CancelledGlobalBanner uses, so dismissing on either
  // surface hides it on both (one cancellation = one dismissal).
  const [cancelledSubId, setCancelledSubId] = useState<string | null>(null);
  const [cancelledBannerDismissed, setCancelledBannerDismissed] = useState(false);
  const [cancelledFadingOut, setCancelledFadingOut] = useState(false);
  // Credit restored but the paid period lapsed while paused (ToU §17.4) — the
  // account needs a fresh cycle bought before services resume.
  const [whitelistingAwaiting, setWhitelistingAwaiting] = useState(false);
  const { open: openReactivation } = useOpenReactivationCheckout();
  // Free + had a previous subscription (CANCELLED most-recent) OR the
  // dev toggle is on. Drives the Welcome-Back heading + Reactivate CTA
  // in the Free-user view.
  const wasPreviouslySubscribed = isFree && (devWasSubscribed || cancelledSubId != null);
  const refetchRealSub = useCallback(() => {
    instance.get("/subscriptions/my")
      .then(({ data }) => setRealSub(data || null))
      .catch(() => setRealSub(null));
    instance.get("/subscriptions/my/history")
      .then(({ data }) => {
        const cancelled = findAnnouncableCancelledSub(data);
        if (cancelled) {
          setCancelledSubId(cancelled.id);
          setCancelledBannerDismissed(
            localStorage.getItem(`cancelled-banner-dismissed-${cancelled.id}`) === "1",
          );
        } else {
          setCancelledSubId(null);
          setCancelledBannerDismissed(false);
        }
      })
      .catch(() => setCancelledSubId(null));
    instance.get("/claims/my/whitelisting-suspension-info")
      .then(({ data }) => setWhitelistingAwaiting(!!data?.awaitingReactivation))
      .catch(() => setWhitelistingAwaiting(false));
  }, []);
  useEffect(() => {
    refetchRealSub();
    instance.get("/claims/my/channel-credits")
      .then(({ data }) => {
        // Endpoint shape: { slots: [{ slotIndex, channel|null, used, limit }], ... }.
        // Map the slots that have a connected channel into the per-channel usage
        // rows the "Claims Used This Month" bars consume.
        const slots: Array<{ channel: { channelName: string | null; youtubeChannelId: string } | null; used?: number; limit?: number }> =
          Array.isArray(data?.slots) ? data.slots : [];
        setRealChannelCredits(
          slots
            .filter((s) => s.channel)
            .map((s) => ({
              channelName: s.channel!.channelName ?? null,
              youtubeChannelId: s.channel!.youtubeChannelId,
              used: s.used ?? 0,
              limit: s.limit ?? 0,
            })),
        );
      })
      .catch(() => setRealChannelCredits([]));
  }, [refetchRealSub]);

  // Real backend state OR dev switcher — pending annual→monthly switch.
  // Without the realSub leg here, the banner never appears in normal flow
  // because devSubState defaults to "active" for non-dev users.
  const isPendingBillingSwitch = devSubState === "pending_switch_to_monthly"
    || realSub?.pendingBillingPeriod === "monthly";
  // Same pattern for `cancelAtPeriodEnd` — real backend state OR dev
  // switcher. Drives the red "Cancelling" banner + the Reactivate CTA.
  // An annual→monthly switch sets `cancelAtPeriodEnd=true` on the Stripe
  // sub (so the annual ends and a new monthly starts) — that's NOT a
  // cancellation from the user's perspective, so suppress the red
  // cancelling banner when a billing-period switch is pending and show
  // only the switch banner instead.
  // Same pattern for past-due — real backend status OR dev switcher.
  const isPastDue = devSubState === "past_due"
    || realSub?.status === "PAST_DUE";
  // PAST_DUE takes precedence over a pending cancellation. A renewal can
  // only FAIL on a sub that's set to renew, so the two states shouldn't
  // co-exist; if the data is inconsistent (e.g. a leftover cancel schedule),
  // the urgent "payment failed" banner wins and the cancelling banner is
  // suppressed so the user never sees two contradictory banners.
  const isCancelling = !isPastDue && (devSubState === "cancelling"
    || (realSub?.cancelAtPeriodEnd === true && realSub?.pendingBillingPeriod !== "monthly"));
  // Trigger fade-in when isCancelling flips false → true mid-session
  // (e.g. after a refetch following the Cancel POST). Initial mount with
  // the banner already showing skips the fade — `prevIsCancellingRef`
  // captures the very first value to detect a transition vs initial.
  const prevIsCancellingRef = useRef(isCancelling);
  useEffect(() => {
    if (!prevIsCancellingRef.current && isCancelling) {
      setEnteringCancelBanner(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setEnteringCancelBanner(false));
      });
    }
    prevIsCancellingRef.current = isCancelling;
  }, [isCancelling]);

  const mockSub = {
    billingPeriod: realSub?.billingPeriod || devBillingPeriod,
    pendingBillingPeriod: realSub?.pendingBillingPeriod ?? (isPendingBillingSwitch ? ("monthly" as const) : null),
    channels: realSub?.channels ?? 2,
    claimsPerChannel: realSub?.claimsPerChannel ?? 15,
    pricePerMonth: Number(realSub?.pricePerMonth ?? 68),
    // Cycle length = period_end − period_start in days. Used as the
    // denominator on monthly upgrade credit proration (matches Stripe's
    // actual calendar-month cycle, 28–31 days, not a hardcoded 30).
    cycleLengthDays: (realSub?.currentPeriodStart && realSub?.currentPeriodEnd)
      ? Math.max(1, Math.round((new Date(realSub.currentPeriodEnd).getTime() - new Date(realSub.currentPeriodStart).getTime()) / 86400000))
      : (realSub?.billingPeriod === "annual" ? 365 : 30),
    currentPeriodEnd: realSub?.currentPeriodEnd
      ? new Date(realSub.currentPeriodEnd).toISOString().slice(0, 10)
      : (() => {
        const d = new Date();
        if (devBillingPeriod === "annual") {
          d.setDate(d.getDate() + 335); // ~11 months left in current year
        } else {
          d.setDate(d.getDate() + 15);  // ~half a monthly cycle left
        }
        return d.toISOString().slice(0, 10);
      })(),
    // Claim-usage card — fed by `/claims/my/channel-credits` (one row per
    // connected YouTube channel). Lifetime claims-used isn't a tracked
    // metric on the backend yet; defaults to 0 until that lands.
    totalClaimsUsed: 0,
    channelUsage: realChannelCredits.map((c) => ({
      name: c.channelName || c.youtubeChannelId,
      used: c.used,
    })),
    // Pending downgrade — real DB columns take precedence; the dev switcher
    // still fills in synthetic values for visual review when no real pending
    // downgrade exists.
    pendingChannels: realSub?.pendingChannels ?? (isPendingDowngrade ? 1 : null as number | null),
    pendingClaimsPerChannel: realSub?.pendingClaimsPerChannel ?? (isPendingDowngrade ? 10 : null as number | null),
    pendingPricePerMonth: realSub?.pendingPricePerMonth ?? (isPendingDowngrade ? 29 : null as number | null),
  };

  return (
    <AccountLayout title="Subscription">
      {/* Green success banner — Reactivate / Keep Current Plan feedback.
          Mirrors the banner on /account/claims. Dismissible via ×.
          ALWAYS rendered (collapsed when no message) so the 0→1 opacity
          transition fires smoothly on first appearance — see comment in
          AccountLicensesPage.renderSuccessAlert for the rationale. The
          "suppress when orange banner already covers this state" rule
          is folded into `collapsed` instead of an outer gate so the
          element stays in the DOM. */}
      {(() => {
        const isDowngradeAlert = successAlert !== null && /^Downgrade scheduled/i.test(successAlert);
        const orangeBannerActive = isSubscribed && mockSub.pendingChannels != null && mockSub.pendingClaimsPerChannel != null;
        const collapsed = successAlert === null || closingAlert || (orangeBannerActive && isDowngradeAlert);
        return (
          <div style={{
            padding: collapsed ? "0 48px 0 18px" : "12px 48px 12px 18px",
            marginBottom: collapsed ? 0 : 12,
            maxHeight: collapsed ? 0 : 200,
            opacity: collapsed ? 0 : 1,
            borderRadius: radius.md,
            background: "#1f8c66", border: "none",
            position: "relative",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            overflow: "hidden",
            transition: "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1), margin-bottom 360ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}>
            <span style={{ fontFamily: fonts.body, fontSize: fontSizes.body, color: "#fff", fontWeight: 500 }}>
              {successAlert ?? ""}
            </span>
            {isDowngradeAlert && (
              <button
                onClick={async () => {
                  try {
                    await instance.post("/stripe/subscriptions/cancel-downgrade");
                    refetchRealSub();
                    setDevSubState("active");
                    dismissSuccessAlert();
                  } catch (err) { console.error("Cancel downgrade failed:", err); }
                }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0,
                  padding: "10px 20px", borderRadius: radius.md,
                  background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
                  border: "none", color: colors.brown.darkest,
                  fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 600,
                  cursor: "pointer", whiteSpace: "nowrap",
                  transition: `all ${transitions.normal}`,
                  boxShadow: "0 0 16px rgba(186, 144, 92, 0.25)",
                }}
              >
                <BlueCheck />
                Keep Current Plan
              </button>
            )}
            <button
              onClick={dismissSuccessAlert}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255, 255, 255, 0.15)"; (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; (e.currentTarget as HTMLButtonElement).style.color = "rgba(255, 255, 255, 0.7)"; }}
              aria-label="Dismiss"
              style={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, borderRadius: "50%", background: "transparent", border: "none", color: "rgba(255, 255, 255, 0.7)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s ease, color 0.15s ease" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        );
      })()}

      {/* DEV: Subscription state switcher — same hoya-only gate as the
          DevSwitcherRail so other users can't get accidentally trapped in
          a synthetic state on the dev box. */}
      {isSubscribed && myData?.email === "hoyaw23479@seosnaps.com" && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
          padding: "8px 12px", borderRadius: radius.md,
          background: "rgba(255, 255, 255, 0.03)", border: "1px dashed rgba(255, 255, 255, 0.15)",
          fontFamily: fonts.body, fontSize: fontSizes.xs, color: colors.text.muted,
          flexWrap: "wrap",
        }}>
          <span>DEV state</span>
          {(["active", "cancelling", "past_due", "pending_downgrade", "pending_switch_to_monthly"] as const).map((s) => {
            const activeBg =
              s === "past_due" ? "rgba(228, 122, 106, 0.2)"
              : s === "cancelling" ? "rgba(245, 166, 35, 0.2)"
              : s === "pending_downgrade" ? "rgba(245, 166, 35, 0.2)"
              : s === "pending_switch_to_monthly" ? "rgba(245, 166, 35, 0.2)"
              : "rgba(156, 211, 253, 0.2)";
            const activeColor =
              s === "past_due" ? colors.accent.red
              : s === "cancelling" ? "#f5a623"
              : s === "pending_downgrade" ? "#f5a623"
              : s === "pending_switch_to_monthly" ? "#f5a623"
              : colors.accent.blue;
            const label =
              s === "pending_switch_to_monthly" ? "pending switch"
              : s.replace(/_/g, " ");
            return (
              <button
                key={s}
                onClick={() => setDevSubState(s)}
                style={{
                  padding: "3px 10px", borderRadius: 6, border: "none", cursor: "pointer",
                  fontFamily: fonts.body, fontSize: fontSizes.xs,
                  background: devSubState === s ? activeBg : "rgba(255, 255, 255, 0.06)",
                  color: devSubState === s ? activeColor : colors.text.muted,
                }}
              >{label}</button>
            );
          })}

        </div>
      )}

      {/* ── Cancelling Banner ── */}
      {isSubscribed && isCancelling && (
        <BannerShell
          data-banner-surface
          style={{
          padding: cancellingFadingOut || enteringCancelBanner ? "0 20px" : "16px 20px",
          marginBottom: cancellingFadingOut || enteringCancelBanner ? 0 : 12,
          maxHeight: cancellingFadingOut || enteringCancelBanner ? 0 : 200,
          opacity: cancellingFadingOut || enteringCancelBanner ? 0 : 1,
          borderRadius: radius.xl,
          ...bannerSurface("rgb(195, 75, 54)"),
          border: "none",
          transition: "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1), margin-bottom 360ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}>
          {(() => {
            const fmtLong = (d: Date) => d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
            const periodEnd = new Date(mockSub.currentPeriodEnd);
            const isAnnualPlan = mockSub.billingPeriod === "annual";
            return (
              <div>
                <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 500, color: "#fff", marginBottom: 4 }}>
                  Your subscription is scheduled for cancellation
                </div>
                <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: "rgba(255, 255, 255, 0.95)" }}>
                  Your {isAnnualPlan ? "annual" : "monthly"} plan is ending on <span style={{ fontFamily: fonts.number, fontWeight: 600, color: "#fff" }}>{fmtLong(periodEnd)}</span>. You'll keep subscription access until then. After that, your account will be downgraded to the Free plan.
                </div>
              </div>
            );
          })()}
          <BannerCTA
            onClick={() => {
              // Sequence the visual: red fades out first, THEN green fades in.
              // POST fires in parallel so backend updates ASAP, but we wait for
              // both the API and the fade-out (Promise.all) before triggering
              // the refetch + success banner so the timing reads smoothly.
              setCancellingFadingOut(true);
              const postPromise = instance.post("/stripe/subscriptions/reactivate")
                .catch((err) => { console.error("Reactivate failed:", err); });
              Promise.all([
                postPromise,
                new Promise((r) => setTimeout(r, 380)),
              ]).then(() => {
                setDevSubState("active");
                const msg = "Cancellation stopped. You're continuing your current plan.";
                writeSubscriptionAlert(successAlertUserId, msg);
                setSuccessAlert(msg);
                refetchRealSub();
                dispatch(my());
                // Reset for any future cancel→keep cycle in the same session.
                window.setTimeout(() => setCancellingFadingOut(false), 100);
              });
            }}
          ><BlueCheck />Keep Subscription</BannerCTA>
        </BannerShell>
      )}

      {/* ── Awaiting Reactivation ── the credit was restored but the paid period
          ran out while the account was paused (ToU §17.4), so continuing means
          buying a fresh cycle. This page is where a customer comes to look at
          their plan, and it knew nothing about the state: only the global bar and
          the Claims page did, so from here the account looked simply broken. */}
      {whitelistingAwaiting && (
        <BannerShell
          data-banner-surface
          style={{
          padding: "16px 20px",
          marginBottom: 12,
          borderRadius: radius.xl,
          ...bannerSurface("rgb(191, 117, 22)"),
          border: "none",
          position: "relative",
        }}>
          <div>
            <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 500, color: "#fff", marginBottom: 4 }}>
              Your billing cycle ended while your account was paused
            </div>
            <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: "rgba(255, 255, 255, 0.95)" }}>
              Every required credit is back in place. Reactivate to start a new billing cycle from today and resume your services.
            </div>
          </div>
          {/* BannerCTA, not a hand-rolled button: the shell centres its children
              vertically, and the old inline `marginTop` pushed this one off that
              centre line. It also brings the same round-arrow glyph the global
              reactivate bar uses, so the two read as the same action. */}
          <BannerCTA onClick={() => void openReactivation()}>
            <ReactivateArrow />
            Reactivate
          </BannerCTA>
        </BannerShell>
      )}

      {/* ── Cancelled Banner ── shown when no active/past-due sub but the
          user's most-recent sub is CANCELLED. Dismissed via the shared
          `cancelled-banner-dismissed-<subId>` localStorage flag — the SAME
          key the global website-wide banner uses, so X-ing it here also
          clears it site-wide (and vice versa). Fade-out animation mirrors the
          `Cancelling` banner below: opacity + max-height + padding +
          margin all collapse over 360ms before the banner unmounts. */}
      {!isSubscribed && cancelledSubId && !cancelledBannerDismissed && (
        <BannerShell style={{
          padding: cancelledFadingOut ? "0 20px" : "16px 20px",
          marginBottom: cancelledFadingOut ? 0 : 12,
          maxHeight: cancelledFadingOut ? 0 : 200,
          opacity: cancelledFadingOut ? 0 : 1,
          borderRadius: radius.xl,
          background: "#1f8c66",
          border: "none",
          position: "relative",
          transition: "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1), margin-bottom 360ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}>
          <div>
            <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 500, color: "#fff", marginBottom: 4 }}>
              Your subscription was cancelled
            </div>
            <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: "rgba(255, 255, 255, 0.95)" }}>
              You're now on the Free plan. Subscribe again below to restore full access.
            </div>
          </div>
          <button
            onClick={() => {
              localStorage.setItem(`cancelled-banner-dismissed-${cancelledSubId}`, "1");
              setCancelledFadingOut(true);
              window.setTimeout(() => setCancelledBannerDismissed(true), 360);
            }}
            aria-label="Dismiss"
            style={{
              position: "absolute", top: 6, right: 10,
              width: 26, height: 26,
              background: "transparent", border: "none",
              color: "rgba(255,255,255,0.85)", fontSize: "1.25rem", lineHeight: 1,
              cursor: "pointer", padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.85)"; }}
          >×</button>
        </BannerShell>
      )}

      {/* ── Past Due Banner ── */}
      {isSubscribed && isPastDue && (
        <BannerShell
          data-banner-surface
          style={{
          padding: "16px 20px", marginBottom: 12,
          borderRadius: radius.xl,
          ...bannerSurface("rgb(195, 75, 54)"),
          border: "none",
        }}>
          <div>
            <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 500, color: "#fff", marginBottom: 4 }}>
              Your last payment failed
            </div>
            <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: "rgba(255, 255, 255, 0.95)" }}>
              Services are stopped. Please update your payment method to continue your subscription. If the issue isn't resolved in the coming days, your account will be downgraded to the Free plan.
            </div>
          </div>
          <BannerCTA onClick={() => openUpdatePaymentModal({ isPastDue: true })}><BlueCard />Update Payment Method</BannerCTA>
        </BannerShell>
      )}

      {/* ── Downgrade Scheduled Banner ── Renders whenever pending fields
          are set, regardless of the dev-state switcher. Real backend data
          flows through `mockSub.pendingChannels` already (see derivation
          above), so this gate covers both real + dev-flipped states. */}
      {isSubscribed && mockSub.pendingChannels != null && mockSub.pendingClaimsPerChannel != null && (
        <BannerShell style={{
          padding: closingDowngradeBanner ? "0 20px" : "16px 20px",
          marginBottom: closingDowngradeBanner ? 0 : 12,
          maxHeight: closingDowngradeBanner ? 0 : 240,
          opacity: closingDowngradeBanner ? 0 : 1,
          borderRadius: radius.xl,
          background: "#c28013",
          border: "none",
          transition: "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1), margin-bottom 360ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}>
          <div>
            <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
              Downgrade scheduled for <span style={{ fontFamily: fonts.number, fontWeight: 700 }}>{new Date(mockSub.currentPeriodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
            </div>
            <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: "rgba(255, 255, 255, 0.85)" }}>
              Your configuration will change from <span style={{ color: "#fff" }}>{mockSub.channels} {mockSub.channels === 1 ? "channel" : "channels"} / {mockSub.claimsPerChannel} monthly claims</span> to <span style={{ color: "#fff" }}>{mockSub.pendingChannels} {mockSub.pendingChannels === 1 ? "channel" : "channels"} / {mockSub.pendingClaimsPerChannel} monthly claims</span>{mockSub.pendingPricePerMonth != null ? <> (${mockSub.pendingPricePerMonth}/mo)</> : null} on <span style={{ color: "#fff" }}>{new Date(mockSub.currentPeriodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>.{mockSub.pendingChannels != null && mockSub.pendingChannels < mockSub.channels && (
                <> <span style={{ color: "#fff" }}>{mockSub.channels - mockSub.pendingChannels} {(mockSub.channels - mockSub.pendingChannels) === 1 ? "channel" : "channels"} will be disconnected.</span></>
              )} You'll keep your current configuration until then.
            </div>
          </div>
          <BannerCTA
            onClick={async () => {
              // Collapse the yellow banner in parallel with the API call,
              // then refresh sub + fire the green success alert. Matches
              // the same orchestration on /account + /account/claims.
              setClosingDowngradeBanner(true);
              try {
                const reqPromise = instance.post("/stripe/subscriptions/cancel-downgrade");
                await new Promise<void>((resolve) => window.setTimeout(resolve, 360));
                await reqPromise;
                refetchRealSub();
                setDevSubState("active");
                const msg = "Downgrade successfully cancelled. You're continuing your current plan and keep your configuration.";
                writeSubscriptionAlert(successAlertUserId, msg);
                setSuccessAlert(msg);
              } catch (err) { console.error("Cancel downgrade failed:", err); }
              finally { setClosingDowngradeBanner(false); }
            }}
          ><BlueCheck />Keep Current Plan</BannerCTA>
        </BannerShell>
      )}

      {/* ── Pending Billing-Period Switch Banner ── Always visible while
          the switch is pending; only /account/claims allows an × dismiss,
          /account and /account/subscription show it until the switch
          actually happens or the user clicks Keep Annual. */}
      {isSubscribed && isPendingBillingSwitch && mockSub.billingPeriod === "annual" && (() => {
        const futureMonthlyTotal = computeSubscriptionPricing(
          subConfig,
          { channels: mockSub.channels, claims: mockSub.claimsPerChannel, isAnnual: false },
        ).totalMonthly;
        const switchDate = new Date(mockSub.currentPeriodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
        return (
          <BannerShell style={{
            padding: "16px 20px", marginBottom: 12,
            borderRadius: radius.xl,
            background: "#c28013",
            border: "none",
          }}>
            <div>
              <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 500, color: "#fff", marginBottom: 4 }}>
                Switching to monthly billing on <span style={{ fontFamily: fonts.number, fontWeight: 600 }}>{switchDate}</span>
              </div>
              <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: "rgba(255, 255, 255, 0.7)" }}>
                After your current annual period ends, you'll be charged <strong style={{ color: "#fff", fontFamily: fonts.number }}>${futureMonthlyTotal}/month</strong>.
              </div>
            </div>
            <BannerCTA
              onClick={async () => {
                try {
                  await instance.post("/stripe/subscriptions/cancel-billing-period-switch");
                  refetchRealSub();
                  dispatch(my());
                  setDevSubState("active");
                  const msg = "Switch cancelled. You're staying on your annual plan.";
                  writeSubscriptionAlert(successAlertUserId, msg);
                  setSuccessAlert(msg);
                } catch (err) { console.error("Cancel switch failed:", err); }
              }}
            ><BlueCheck />Keep Annual</BannerCTA>
          </BannerShell>
        );
      })()}

      {/* ── Status (free users only) ── */}
      {isFree && (
        <>
          <StatusBanner $active={false}>
            <PlanTitle style={{ marginBottom: 8 }}>Your Current Plan</PlanTitle>
            <PillDescRow>
              <span style={{
                display: "inline-block", flexShrink: 0,
                padding: "6px 18px", borderRadius: "10px",
                fontFamily: fonts.number, fontSize: "1rem", fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "1px",
                background: colors.tier.free.bg,
                color: colors.tier.free.text,
              }}>Free</span>
              <StatusDesc style={{ fontSize: fontSizes.body, lineHeight: 1.5, margin: 0 }}>{status.desc}</StatusDesc>
            </PillDescRow>
          </StatusBanner>
        </>
      )}

      {/* DEV: free "previously subscribed" toggle — hoya-only gate (same as the
          DEV state switcher above) so other accounts don't see a dev control. */}
      {isFree && myData?.email === "hoyaw23479@seosnaps.com" && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
          padding: "8px 12px", borderRadius: radius.md,
          background: "rgba(255, 255, 255, 0.03)", border: "1px dashed rgba(255, 255, 255, 0.15)",
          fontFamily: fonts.body, fontSize: fontSizes.xs, color: colors.text.muted,
        }}>
          <span>DEV</span>
          <button
            onClick={() => setDevWasSubscribed(!devWasSubscribed)}
            style={{
              padding: "3px 10px", borderRadius: 6, border: "none", cursor: "pointer",
              fontFamily: fonts.body, fontSize: fontSizes.xs,
              background: devWasSubscribed ? "rgba(156, 211, 253, 0.2)" : "rgba(255, 255, 255, 0.06)",
              color: devWasSubscribed ? colors.accent.blue : colors.text.muted,
            }}
          >previously subscribed</button>
        </div>
      )}

      {/* ═══════════════════════════════════════
           FREE USER VIEW
         ═══════════════════════════════════════ */}
      {isFree && (
        <SectionCard $accent>
          <PlanTitle style={{ marginBottom: 20 }}><PremiumTitlePill>Premium</PremiumTitlePill>{wasPreviouslySubscribed ? " Restart Your Subscription" : " Subscription — Start Today"}</PlanTitle>

          <TwoColLayout>
            {/* Left: config */}
            <div style={{ display: "flex", flexDirection: "column", padding: "20px 24px", background: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.06)", borderRadius: "12px" }}>
              <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, color: colors.text.muted, marginBottom: 12 }}>Adjust your needs</div>
              {/* Steppers use the SAME components as the choose-option modal
                  (WhitelistingStepper/Btn/Text) so this box's pickers look
                  identical to it — solid-blue +/- on a slim pill, [ − value + ]. */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                <WhitelistingStepper>
                  <WhitelistingBtn onClick={() => setChannels(Math.max(subConfig.includedChannels, channels - 1))} style={{ visibility: channels <= subConfig.includedChannels ? "hidden" : "visible" }}>−</WhitelistingBtn>
                  <WhitelistingText style={{ flex: 1, minWidth: 0, textAlign: "center", whiteSpace: "normal" }}>{channels} YouTube {channels === 1 ? "channel" : "channels"}</WhitelistingText>
                  <WhitelistingBtn onClick={() => setChannels(channels + 1)}>+</WhitelistingBtn>
                </WhitelistingStepper>
                <WhitelistingStepper>
                  <WhitelistingBtn onClick={() => setClaims(Math.max(subConfig.includedClaims, claims - subConfig.claimIncrement))} style={{ visibility: claims <= subConfig.includedClaims ? "hidden" : "visible" }}>−</WhitelistingBtn>
                  <WhitelistingText style={{ flex: 1, minWidth: 0, textAlign: "center", whiteSpace: "normal" }}>{claims} monthly claims{channels > 1 ? " per channel" : ""}</WhitelistingText>
                  <WhitelistingBtn onClick={() => setClaims(Math.min(200, claims + subConfig.claimIncrement))}>+</WhitelistingBtn>
                </WhitelistingStepper>
              </div>

              {/* Billing toggle + price — matches the choose-option modal: the
                  toggle sits on its own row above the price (never squeezed
                  beside it, which made "Annual (Save 10%)" wrap), and the
                  channel/claim config is already stated by the steppers + the
                  feature list, so no separate Channels/Claims summary. */}
              <div style={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", marginTop: 8, paddingTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                <BillingToggleWrap>
                  <BillingSwitch $annual={isAnnual} onClick={() => setBilling(isAnnual ? "monthly" : "annual")} />
                  <BillingLabel $active={isAnnual} onClick={() => setBilling("annual")} style={{ whiteSpace: "nowrap" }}>
                    Annual (Save {subConfig.annualDiscountPercent}%)
                  </BillingLabel>
                </BillingToggleWrap>
                <PriceBlock>
                  <PriceValue>${monthlyTotal}</PriceValue>
                  <PriceUnit>/month</PriceUnit>
                </PriceBlock>
              </div>

              <div style={{ flex: 1 }} />
              <CTAButton style={{ marginTop: 20 }} onClick={() => dispatch(openSubscriptionCheckout({ channels, claims, billingPeriod: billing }))}>
                {wasPreviouslySubscribed
                  ? <ButtonLabel glyph="redo" iconFirst>Reactivate</ButtonLabel>
                  : "Get Started"}
              </CTAButton>
            </div>

            {/* Right: features */}
            <FeatureCol>
              <FeatureItem><FeatureDot $type="check">&#10003;</FeatureDot>Full music library access</FeatureItem>
              <FeatureItem><FeatureDot $type="check">&#10003;</FeatureDot>Covers {channels} YouTube {channels === 1 ? "channel" : "channels"}</FeatureItem>
              <FeatureItem><FeatureDot $type="check">&#10003;</FeatureDot>{claims} monthly claims{channels > 1 ? ` per channel (${claims * channels} total)` : ""}</FeatureItem>
              <FeatureItem><FeatureDot $type="check">&#10003;</FeatureDot>MP3 + WAV downloads</FeatureItem>
              <FeatureItem><FeatureDot $type="check">&#10003;</FeatureDot>Monetization allowed</FeatureItem>
              <FeatureItem><FeatureDot $type="check">&#10003;</FeatureDot>1-line credit text</FeatureItem>
              <FeatureItem><FeatureDot $type="check">&#10003;</FeatureDot>Auto-resolve claims</FeatureItem>
              <FeatureItem><FeatureDot $type="check">&#10003;</FeatureDot>Cancel anytime</FeatureItem>
              <FeatureItem><FeatureDot $type="cross">&#10007;</FeatureDot>No music or ambient content</FeatureItem>
              <FeatureItem><FeatureDot $type="cross">&#10007;</FeatureDot>No use in livestreams</FeatureItem>
            </FeatureCol>
          </TwoColLayout>
        </SectionCard>
      )}

      {/* ═══════════════════════════════════════
           ACTIVE SUBSCRIBER VIEW
         ═══════════════════════════════════════ */}
      {isSubscribed && (
        <>
          {/* Phone/tablet: drop the 20px top gap so the first box sits the same
              distance below the "Subscription" heading as the free view's
              StatusBanner (which has no top margin). Desktop keeps the 20px. */}
          <SectionCard $accent style={{ marginTop: isCompact ? 0 : 20 }}>
            <PlanTitle style={{ marginBottom: isCompact ? 8 : 14 }}>{mockSub.billingPeriod === "annual" ? "Annual" : "Monthly"} Subscription</PlanTitle>

            {/* Configuration summary — boxed to match the cancellation card style */}
            <div style={{
              padding: "16px 18px",
              borderRadius: radius.xl,
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              fontFamily: fonts.body,
              fontSize: fontSizes.body,
              color: "#fff",
              lineHeight: 1.6,
            }}>
              <div>{mockSub.channels} {mockSub.channels === 1 ? "channel" : "channels"}</div>
              <div style={{ marginBottom: 14 }}>
                {mockSub.claimsPerChannel} monthly claims{mockSub.channels > 1 ? " per channel" : ""}
              </div>
              <div style={{
                paddingTop: 14,
                borderTop: "1px solid rgba(255, 255, 255, 0.12)",
              }}>
                <span style={{ fontFamily: fonts.number, fontWeight: 700 }}>
                  {(() => {
                    const isAnnualSub = mockSub.billingPeriod === "annual";
                    // Round at each aggregation so 26.10 × 12 doesn't
                    // surface as 313.20000000000005 (JS float quirk).
                    const monthlyEq = computeSubscriptionPricing(
                      subConfig,
                      { channels: mockSub.channels, claims: mockSub.claimsPerChannel, isAnnual: isAnnualSub },
                    ).totalMonthly;
                    const annualEq = Math.round(monthlyEq * 12 * 100) / 100;
                    return isAnnualSub ? `$${annualEq.toFixed(2)}/year` : `$${monthlyEq.toFixed(2)}/mo`;
                  })()}
                </span>
                <span style={{ color: colors.text.muted, margin: "0 10px" }}>·</span>
                <span style={{ color: colors.text.muted }}>next billing on {new Date(mockSub.currentPeriodEnd).toLocaleDateString("en-US", { month: "short", day: "numeric", year: mockSub.billingPeriod === "annual" ? "numeric" : undefined })}</span>
              </div>
              <button
                onClick={() => {
                  // Awaiting reactivation: there is no running cycle to
                  // reconfigure, and the reactivation checkout is where the new
                  // configuration is picked — so go straight there.
                  if (whitelistingAwaiting) { void openReactivation(); return; }
                  setManageInitialTab("config");
                  setManageOpen(true);
                }}
                style={{
                  marginTop: 16,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 22px",
                  borderRadius: radius.md,
                  // The Subscription pill's gradient, not the gold button one.
                  // This button sits directly under that pill and names the same
                  // thing, so the two read as one control rather than as a plan
                  // badge with an unrelated gold CTA beside it. White label and
                  // white diamond — the deep ground cannot carry the dark text
                  // or the pale-blue glyph the gold buttons use.
                  background: colors.tier.subscription.bg,
                  border: "none",
                  color: colors.tier.subscription.text,
                  fontFamily: fonts.body,
                  fontSize: fontSizes.body,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: `all ${transitions.normal}`,
                  boxShadow: "0 0 16px rgba(60, 130, 165, 0.28)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 24px rgba(60, 130, 165, 0.5)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 0 16px rgba(60, 130, 165, 0.28)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <BlueDiamond color={colors.tier.subscription.text} />
                Manage Subscription
              </button>
            </div>

            {/* ── Claims Usage (merged into Your Configuration card so the
                "this is what you're paying for" + "this is what you've
                used" read as one band of plan state). */}
            <PlanTitle style={{ marginTop: 24, marginBottom: isCompact ? 8 : 14 }}>Claims Used This Month</PlanTitle>
            {mockSub.channelUsage.length === 0 ? (
              /* Glassy CTA box matching the design-system spec:
                   padding: 16px 18px; border-radius: 12px;
                   background: rgba(255,255,255,0.05);
                   border: 1px solid rgba(255,255,255,0.12); */
              <div style={{
                padding: "16px 18px",
                borderRadius: 12,
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}>
                <a
                  href="/account/claims"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 22px",
                    borderRadius: radius.md,
                    background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
                    border: "none",
                    color: colors.brown.darkest,
                    fontFamily: fonts.body,
                    fontSize: fontSizes.body,
                    fontWeight: 600,
                    // Match the <button>'s UA line-height so the anchor isn't
                    // taller (anchors otherwise inherit the page's larger value).
                    lineHeight: "normal",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: `all ${transitions.normal}`,
                    boxShadow: "0 0 16px rgba(186, 144, 92, 0.25)",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 24px rgba(186, 144, 92, 0.5)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 16px rgba(186, 144, 92, 0.25)"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <BluePlus />
                  Connect Channels
                </a>
              </div>
            ) : (
              /* Bordered box mirroring the Configuration card above, with one
                 row per channel: name on top, usage bar + used/limit count
                 to the right. Bar gradient + count color both pct-driven
                 (matches /account/claims). */
              <div style={{
                padding: "16px 18px",
                borderRadius: radius.xl,
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}>
                {mockSub.channelUsage.map((ch) => {
                  const pct = Math.round((ch.used / mockSub.claimsPerChannel) * 100);
                  const countColor =
                    pct >= 100 ? colors.accent.red
                    : pct >= 80 ? "#f5a623"
                    : colors.text.muted;
                  return (
                    <div key={ch.name}>
                      <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: colors.text.primary, marginBottom: 6 }}>
                        {ch.name}
                      </div>
                      <UsageBar>
                        <UsageTrack>
                          <UsageFill $pct={pct} />
                        </UsageTrack>
                        <UsageText style={{ minWidth: 0, fontSize: fontSizes.xs, color: countColor }}>
                          {ch.used}/{mockSub.claimsPerChannel}
                        </UsageText>
                      </UsageBar>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <span style={{ fontFamily: fonts.number, fontSize: fontSizes.sm, color: colors.text.muted }}>
                {mockSub.totalClaimsUsed} claims used all time
              </span>
            </div>
          </SectionCard>
        </>
      )}



      {/* ── Music Creator Program ── */}
      {accountType !== AccountType.MUSIC_CREATOR && (
        <SectionCard style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <div style={{
              width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
              background: "rgba(156, 211, 253, 0.08)", border: "1px solid rgba(156, 211, 253, 0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.accent.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: fonts.body, fontSize: fontSizes.lg, fontWeight: 600, color: colors.text.primary, marginBottom: 4 }}>
                Are you creating music or ambient content on YouTube?
              </div>
              <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, color: colors.text.muted, lineHeight: 1.6, marginBottom: 12 }}>
                Join our Music Creator Program and start earning revenue with our tracks. Get your claims auto-resolved, use our full catalog with credit text, and grow your channel.
              </div>
              <a
                href="/music-creator"
                style={{
                  fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 500,
                  color: colors.accent.blue, textDecoration: "none", textUnderlineOffset: "3px",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
              >
                Learn more about the Music Creator Program →
              </a>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ── Change Configuration Modal ── */}
      <ManageAddonsModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        initialTab={manageInitialTab}
        isPastDue={isPastDue}
        isAwaitingReactivation={whitelistingAwaiting}
        onReactivateNow={() => { setManageOpen(false); void openReactivation(); }}
        isCancelling={isCancelling}
        onReactivate={() => {
          // Close the modal first so the page-level success banner is visible.
          setManageOpen(false);
          // Optimistic UI: flip state + show banner immediately. The API call
          // runs in the background; failures are logged but don't block the
          // success feedback (state will be re-fetched on next page load).
          setDevSubState("active");
          const msg = "Cancellation stopped. You're continuing your current plan.";
          writeSubscriptionAlert(successAlertUserId, msg);
          setSuccessAlert(msg);
          instance.post("/stripe/subscriptions/reactivate")
            .then(() => { refetchRealSub(); dispatch(my()); })
            .catch((err) => { console.error("Reactivate failed:", err); });
        }}
        onScheduleDowngrade={(newChannels, newClaims) => {
          // Fire-and-forget the update. PendingDowngradeGlobalModal
          // (mounted in SecondHeader) listens for the
          // `pending-downgrade-changed` event and auto-opens the keep-list
          // picker if channels are being reduced, so we don't need to
          // open it locally here.
          instance.post("/stripe/subscriptions/update", { channels: newChannels, claims: newClaims })
            .then(() => {
              refetchRealSub();
              window.dispatchEvent(new Event("pending-downgrade-changed"));
            })
            .catch((err) => { console.error("Schedule downgrade failed:", err); });
        }}
        onSwitchToMonthly={() => {
          // POST + refresh page-local sub data AND Redux user so the
          // pending-switch banner appears + the header / user menu stay
          // accurate without a hard page reload (which causes a brief
          // "Free Plan" flash while Redux re-hydrates).
          instance.post("/stripe/subscriptions/switch-to-monthly")
            .then(() => {
              refetchRealSub();
              dispatch(my());
              // The confirmation banner renders at the top of the page; the modal
              // closes leaving the user scrolled down. Bring them to the top so
              // they see the banner immediately.
              window.scrollTo({ top: 0, behavior: "smooth" });
            })
            .catch((err) => { console.error("Switch to monthly failed:", err); });
        }}
        onCancelBillingPeriodSwitch={() => {
          instance.post("/stripe/subscriptions/cancel-billing-period-switch")
            .then(() => {
              refetchRealSub();
              dispatch(my());
              const msg = "Switch cancelled. You're staying on your annual plan.";
              writeSubscriptionAlert(successAlertUserId, msg);
              setSuccessAlert(msg);
              window.scrollTo({ top: 0, behavior: "smooth" });
            })
            .catch((err) => { console.error("Cancel billing-period switch failed:", err); });
        }}
        currentChannels={mockSub.channels}
        currentClaims={mockSub.claimsPerChannel}
        billingPeriod={mockSub.billingPeriod}
        daysLeftInPeriod={Math.max(0, Math.ceil((new Date(mockSub.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))}
        cycleLengthDays={mockSub.cycleLengthDays}
        periodEndDate={mockSub.currentPeriodEnd}
        pendingBillingPeriod={mockSub.pendingBillingPeriod}
      />
      <UpdatePaymentMethodModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        isPastDue={isPastDue}
        failedAmount={mockSub.pricePerMonth}
        failedDate={mockSub.currentPeriodEnd}
      />
    </AccountLayout>
  );
};

export default AccountSubscription;
